import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";
import {
  GetApplicationEmployees,
  GetApplicationList,
  GetApplicationsModulesList,
  GetApplicationsSubModulesList,
  AddOrEditApplicationVersion,
  DeleteApplicationVersion,
  GetApplicationVersionById,
  GetApplicationVersionList,
} from "../../../services/admin";

const EMPTY_ARRAY = [];

const releaseTypes = ["Major", "Minor", "Patch", "Hotfix"];
const releaseStatuses = ["Planning", "Development", "Testing", "Release"];
const environments = ["Development", "Production"];


const DEPLOYMENT_CHECKLIST_OPTIONS = [
  {
    id: "DATABASE_BACKUP_CONFIRMED",
    name: "Database backup confirmed",
    description: "Required database backups are completed and verified.",
  },
  {
    id: "DATABASE_SCRIPTS_REVIEWED",
    name: "Database scripts reviewed",
    description: "Table, stored procedure, function, type, index, and migration scripts are reviewed.",
  },
  {
    id: "CONFIGURATION_UPDATED",
    name: "Configuration updated",
    description: "Required environment and application configuration changes are ready.",
  },
  {
    id: "BUILD_VERIFIED",
    name: "Build published and verified",
    description: "The release build is generated and verified for the target environment.",
  },
  {
    id: "SMOKE_TEST_COMPLETED",
    name: "Smoke testing completed",
    description: "Critical application flows are smoke-tested successfully.",
  },
  {
    id: "ROLLBACK_PLAN_READY",
    name: "Rollback plan is ready",
    description: "Rollback scripts, restore steps, and responsible owners are confirmed.",
  },
];

const EMPTY_FORM_VALUES = {
  applicationId: "",
  versionNumber: "",
  releaseName: "",
  releaseType: "",
  status: "Planning",
  buildNumber: "",
  environment: "Development",
  deploymentDate: "",
  developedBy: [],
  testedBy: [],
  approvedBy: [],
  teamLeadId: "",
  gitBranch: "",
  versionDescription: "",
  features: [],
  databaseTablesAdded: [],
  databaseTablesModified: [],
  databaseStoredProceduresAdded: [],
  databaseStoredProceduresModified: [],
  dataMigration: false,
  dataMigrationSourceDatabaseOption: "",
  dataMigrationSourceCustomDatabaseName: "",
  dataMigrationTargetDatabaseOption: "",
  dataMigrationTargetCustomDatabaseName: "",
  dataMigrationTables: [],
  dataMigrationStoredProcedures: [],
  dataMigrationDescription: "",
  deploymentChecklist: [],
  remarks: "",
};

const getApiData = (response) =>
  response?.data?.data ?? response?.data ?? response ?? {};

const getApiMessage = (response, fallback) => {
  const data = getApiData(response);
  return data?.message ?? data?.Message ?? fallback;
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return (
    data?.message ??
    data?.Message ??
    data?.detail ??
    data?.title ??
    error?.message ??
    fallback
  );
};

const getValue = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }
  return undefined;
};

const uniqueStringIds = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value ?? "").trim())
        .filter(Boolean),
    ),
  );

const uniqueOptionsById = (options = []) => {
  const map = new Map();

  options.forEach((option) => {
    const id = String(option?.id ?? "").trim();

    if (id && !map.has(id)) {
      map.set(id, option);
    }
  });

  return Array.from(map.values());
};

const getParticipantIdsByRole = (participants, role) =>
  uniqueStringIds(
    (participants || [])
      .filter(
        (item) =>
          String(
            getValue(
              item,
              "participantType",
              "ParticipantType",
            ) || "",
          ).toUpperCase() === role,
      )
      .map((item) =>
        getValue(
          item,
          "personId",
          "PersonId",
          "personID",
          "PersonID",
        ),
      ),
  );

const getCollection = (item, ...keys) => {
  const value = getValue(item, ...keys);
  return Array.isArray(value) ? value : [];
};

const toDateInputValue = (value) => {
  if (!value) return "";

  if (
    value instanceof Date &&
    !Number.isNaN(value.getTime())
  ) {
    const year = value.getFullYear();

    const month = String(
      value.getMonth() + 1,
    ).padStart(2, "0");

    const day = String(
      value.getDate(),
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const rawValue = String(value).trim();

  if (!rawValue) return "";

  /*
   * API format:
   * 21-07-2026 04:45:00 PM
   * 21-07-2026
   */
  const dayFirstDateMatch = rawValue.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:\s|$)/,
  );

  if (dayFirstDateMatch) {
    const [, day, month, year] =
      dayFirstDateMatch;

    return `${year}-${month}-${day}`;
  }

  /*
   * ISO / SQL formats:
   * 2026-07-21
   * 2026-07-21T16:45:00
   * 2026-07-21 16:45:00
   */
  const yearFirstDateMatch = rawValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T|\s|$)/,
  );

  if (yearFirstDateMatch) {
    const [, year, month, day] =
      yearFirstDateMatch;

    return `${year}-${month}-${day}`;
  }

  /*
   * Newtonsoft format:
   * /Date(1784652300000)/
   */
  const dotNetDateMatch = rawValue.match(
    /^\/Date\((-?\d+)(?:[+-]\d{4})?\)\/$/,
  );

  if (dotNetDateMatch) {
    const parsedDate = new Date(
      Number(dotNetDateMatch[1]),
    );

    if (!Number.isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();

      const month = String(
        parsedDate.getMonth() + 1,
      ).padStart(2, "0");

      const day = String(
        parsedDate.getDate(),
      ).padStart(2, "0");

      return `${year}-${month}-${day}`;
    }
  }

  return "";
};


const parseSemanticVersion = (value) => {
  const match = String(value ?? "")
    .trim()
    .match(/^v?(\d+)\.(\d+)\.(\d+)$/i);

  if (!match) return null;

  return {
    major: Number(match[1]),
    minor: Number(match[2]),
    patch: Number(match[3]),
  };
};

const compareSemanticVersions = (first, second) => {
  if (first.major !== second.major) {
    return first.major - second.major;
  }

  if (first.minor !== second.minor) {
    return first.minor - second.minor;
  }

  return first.patch - second.patch;
};

const getVersionNumberFromRecord = (item) =>
  getValue(
    item,
    "versionNumber",
    "VersionNumber",
    "applicationVersion",
    "ApplicationVersion",
  );

const getNextSemanticVersion = (records = [], releaseType = "") => {
  const semanticVersions = (records || [])
    .map((item) =>
      parseSemanticVersion(
        getVersionNumberFromRecord(item),
      ),
    )
    .filter(Boolean)
    .sort(compareSemanticVersions);

  if (!semanticVersions.length) {
    return "1.0.0";
  }

  const latest =
    semanticVersions[semanticVersions.length - 1];

  switch (
  String(releaseType || "")
    .trim()
    .toUpperCase()
  ) {
    case "MAJOR":
      return `${latest.major + 1}.0.0`;

    case "MINOR":
      return `${latest.major}.${latest.minor + 1}.0`;

    case "PATCH":
    case "HOTFIX":
      return `${latest.major}.${latest.minor}.${latest.patch + 1}`;

    default:
      return `${latest.major}.${latest.minor}.${latest.patch}`;
  }
};

const getTotalRecordCount = (response, records = []) => {
  const responseData = response?.data ?? response ?? {};

  return Number(
    responseData.totalRecords ??
    responseData.TotalRecords ??
    responseData?.data?.totalRecords ??
    responseData?.data?.TotalRecords ??
    response?.totalRecords ??
    response?.TotalRecords ??
    records[0]?.totalRecords ??
    records[0]?.TotalRecords ??
    records.length,
  ) || 0;
};

const VersionViewSection = ({
  icon,
  title,
  subtitle,
  count,
  children,
}) => (
  <Paper
    variant="outlined"
    sx={{
      overflow: "hidden",
      borderRadius: 1.5,
      borderColor: "divider",
      boxShadow: "none",
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.5}
      sx={{
        px: 2,
        py: 1.4,
        bgcolor: "primary.light",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.1}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            display: "grid",
            placeItems: "center",
            borderRadius: 1,
            bgcolor: "background.paper",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon={icon} size="17" />
        </Box>

        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            color="primary.main"
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {count !== undefined && (
        <Chip
          size="small"
          label={count}
          sx={{
            bgcolor: "background.paper",
            fontWeight: 700,
          }}
        />
      )}
    </Stack>

    <Box sx={{ p: 2 }}>
      {children}
    </Box>
  </Paper>
);

const URL_SPLIT_PATTERN = /(https?:\/\/[^\s<>"']+)/gi;
const URL_EXACT_PATTERN = /^https?:\/\/[^\s<>"']+$/i;
const URL_TRAILING_PUNCTUATION_PATTERN = /[),.;!?]+$/;

const renderTextWithClickableLinks = (value) =>
  String(value).split(URL_SPLIT_PATTERN).map((part, index) => {
    if (!URL_EXACT_PATTERN.test(part)) {
      return <span key={`text-${index}`}>{part}</span>;
    }

    const trailingPunctuation =
      part.match(URL_TRAILING_PUNCTUATION_PATTERN)?.[0] || "";
    const url = trailingPunctuation
      ? part.slice(0, -trailingPunctuation.length)
      : part;

    return (
      <span key={`url-${index}`}>
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          color="primary.main"
          underline="hover"
          sx={{
            fontWeight: 700,
            overflowWrap: "anywhere",
            cursor: "pointer",
          }}
        >
          {url}
        </Link>
        {trailingPunctuation}
      </span>
    );
  });

const VersionViewField = ({
  label,
  value,
  icon,
  mono = false,
  fullWidth = false,
  linkify = false,
}) => {
  const hasValue =
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "";

  return (
    <Grid
      item
      xs={12}
      sm={fullWidth ? 12 : 6}
      md={fullWidth ? 12 : 4}
    >
      <Paper
        variant="outlined"
        sx={{
          height: "100%",
          p: 1.35,
          borderRadius: 1,
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
        >
          {icon && (
            <Box
              sx={{
                mt: 0.15,
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <FeatherIcon icon={icon} size="16" />
            </Box>
          )}

          <Box sx={{ minWidth: 0, width: "100%" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {label}
            </Typography>

            <Typography
              component="div"
              variant="body2"
              fontWeight={700}
              sx={{
                mt: 0.25,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: mono
                  ? "Consolas, Monaco, monospace"
                  : "inherit",
              }}
            >
              {!hasValue
                ? "-"
                : linkify
                  ? renderTextWithClickableLinks(value)
                  : String(value)}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );
};

const VersionViewEmpty = ({ message }) => (
  <Paper
    variant="outlined"
    sx={{
      py: 2.5,
      px: 2,
      textAlign: "center",
      borderStyle: "dashed",
      color: "text.secondary",
      bgcolor: "action.hover",
    }}
  >
    <FeatherIcon icon="inbox" size="20" />
    <Typography variant="body2" sx={{ mt: 0.5 }}>
      {message}
    </Typography>
  </Paper>
);

const renderVersionDetailChips = (details = []) => {
  if (!details.length) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
      >
        No related functions, types, or indexes.
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      useFlexGap
      flexWrap="wrap"
      gap={0.75}
    >
      {details.map((detail, index) => {
        const detailType = String(
          getValue(
            detail,
            "detailType",
            "DetailType",
          ) || "",
        ).toUpperCase();

        const detailName =
          getValue(
            detail,
            "detailName",
            "DetailName",
          ) || "-";

        return (
          <Chip
            key={`${detailType}-${detailName}-${index}`}
            size="small"
            variant="outlined"
            label={`${detailType}: ${detailName}`}
            sx={{
              fontFamily:
                "Consolas, Monaco, monospace",
            }}
          />
        );
      })}
    </Stack>
  );
};

const getSchemaSelection = (schemaName) => {
  const value = String(schemaName || "").trim();
  const standard = DATABASE_SCHEMA_OPTIONS.some(
    (item) => item.value !== "OTHER" && item.value.toLowerCase() === value.toLowerCase(),
  );
  return {
    schemaOption: standard ? value.toLowerCase() : value ? "OTHER" : "",
    customSchemaName: standard ? "" : value,
  };
};

const getDatabaseSelection = (databaseName) => {
  const value = String(databaseName || "").trim();
  const standard = DATABASE_OPTIONS.some(
    (item) => item.value !== "OTHER" && item.value.toLowerCase() === value.toLowerCase(),
  );
  const selected = DATABASE_OPTIONS.find(
    (item) => item.value !== "OTHER" && item.value.toLowerCase() === value.toLowerCase(),
  );
  return {
    option: standard ? selected?.value || value : value ? "OTHER" : "",
    customName: standard ? "" : value,
  };
};

const createObjectDetails = (row, includeIndexes) => {
  let sortOrder = 1;
  const details = [];
  normalizeNameList(row.functionNames).forEach((detailName) => {
    details.push({ detailType: "FUNCTION", detailName, sortOrder: sortOrder++ });
  });
  normalizeNameList(row.typeNames).forEach((detailName) => {
    details.push({ detailType: "TYPE", detailName, sortOrder: sortOrder++ });
  });
  if (includeIndexes) {
    normalizeNameList(row.indexNames).forEach((detailName) => {
      details.push({ detailType: "INDEX", detailName, sortOrder: sortOrder++ });
    });
  }
  return details;
};

const createDatabaseChangePayload = (rows, objectType, changeType, includeIndexes) =>
  (rows || []).map((row, index) => ({
    objectType,
    changeType,
    databaseName: null,
    schemaName: resolveDatabaseSchemaName(row),
    objectName: String(row.objectName || "").trim(),
    description: String(row.description || "").trim(),
    sortOrder: index + 1,
    details: createObjectDetails(row, includeIndexes),
  }));

const createMigrationObjectPayload = (object, includeIndexes, index) => ({
  schemaName: resolveMigrationSchemaName(
    object.schemaOption,
    object.customSchemaName,
  ),
  objectName: String(object.objectName || "").trim(),
  description: String(object.description || "").trim(),
  sortOrder: index + 1,
  details: createObjectDetails(object, includeIndexes),
});

const createMigrationMappingPayload = (
  rows,
  objectType,
  includeIndexes,
  startingMappingNumber,
) =>
  (rows || []).map((mapping, index) => ({
    mappingNumber: startingMappingNumber + index,
    objectType,
    sourceObjects: (mapping.sourceObjects || []).map((object, objectIndex) =>
      createMigrationObjectPayload(object, includeIndexes, objectIndex),
    ),
    targetObjects: (mapping.targetObjects || []).map((object, objectIndex) =>
      createMigrationObjectPayload(object, includeIndexes, objectIndex),
    ),
  }));

const buildSavePayload = (values, applicationVersionId, expectedRowVersion) => {
  const developedByIds = uniqueStringIds(
    values.developedBy,
  );

  const testedByIds = uniqueStringIds(
    values.testedBy,
  );

  const approvedByIds = uniqueStringIds(
    values.approvedBy,
  );

  const tableMappings = createMigrationMappingPayload(
    values.dataMigrationTables,
    "TABLE",
    true,
    1,
  );
  const storedProcedureMappings = createMigrationMappingPayload(
    values.dataMigrationStoredProcedures,
    "STORED_PROCEDURE",
    false,
    tableMappings.length + 1,
  );

  const sourceDatabaseName = resolveMigrationDatabaseName(
    values.dataMigrationSourceDatabaseOption,
    values.dataMigrationSourceCustomDatabaseName,
  );
  const targetDatabaseName = resolveMigrationDatabaseName(
    values.dataMigrationTargetDatabaseOption,
    values.dataMigrationTargetCustomDatabaseName,
  );

  return {
    applicationVersionId: Number(applicationVersionId || 0),
    applicationId: Number(values.applicationId),
    versionNumber: values.versionNumber.trim(),
    releaseName: values.releaseName.trim(),
    releaseType: values.releaseType,
    status: values.status,
    buildNumber: values.buildNumber.trim(),
    environment: values.environment,
    deploymentDateTime: values.deploymentDate,
    teamLeadPersonId: Number(values.teamLeadId),
    gitBranch: values.gitBranch.trim(),
    versionDescription: values.versionDescription.trim(),
    remarks: values.remarks.trim() || null,
    participants: [
      ...developedByIds.map((personId, index) => ({
        personId: Number(personId),
        participantType: "DEVELOPER",
        sortOrder: index + 1,
      })),

      ...testedByIds.map((personId, index) => ({
        personId: Number(personId),
        participantType: "TESTER",
        sortOrder: index + 1,
      })),

      ...approvedByIds.map((personId, index) => ({
        personId: Number(personId),
        participantType: "APPROVER",
        sortOrder: index + 1,
      })),
    ],
    features: values.features.map((feature, index) => ({
      moduleId: Number(feature.moduleId),
      subModuleId: feature.subModuleId ? Number(feature.subModuleId) : null,
      featureDescription: String(feature.feature || "").trim(),
      sortOrder: index + 1,
    })),
    databaseChanges: [
      ...createDatabaseChangePayload(values.databaseTablesAdded, "TABLE", "ADDED", true),
      ...createDatabaseChangePayload(values.databaseTablesModified, "TABLE", "MODIFIED", true),
      ...createDatabaseChangePayload(
        values.databaseStoredProceduresAdded,
        "STORED_PROCEDURE",
        "ADDED",
        false,
      ),
      ...createDatabaseChangePayload(
        values.databaseStoredProceduresModified,
        "STORED_PROCEDURE",
        "MODIFIED",
        false,
      ),
    ],
    dataMigrations: values.dataMigration
      ? [
        {
          sourceDatabaseName,
          targetDatabaseName,
          migrationDescription: values.dataMigrationDescription.trim(),
          mappings: [...tableMappings, ...storedProcedureMappings],
        },
      ]
      : [],
    checklistItems: DEPLOYMENT_CHECKLIST_OPTIONS.filter((item) =>
      values.deploymentChecklist.includes(item.id),
    ).map((item) => ({
      checklistKey: item.id,
      checklistLabel: item.name,
    })),
    expectedRowVersion: expectedRowVersion || null,
  };
};

const mapDetailNames = (details, parentId) => {
  const names = { functionNames: [], typeNames: [], indexNames: [] };
  details
    .filter(
      (detail) =>
        String(
          getValue(
            detail,
            "applicationVersionDatabaseObjectId",
            "ApplicationVersionDatabaseObjectId",
            "applicationVersionDataMigrationObjectId",
            "ApplicationVersionDataMigrationObjectId",
          ),
        ) === String(parentId),
    )
    .sort(
      (first, second) =>
        Number(getValue(first, "sortOrder", "SortOrder") || 0) -
        Number(getValue(second, "sortOrder", "SortOrder") || 0),
    )
    .forEach((detail) => {
      const detailType = String(getValue(detail, "detailType", "DetailType") || "").toUpperCase();
      const detailName = String(getValue(detail, "detailName", "DetailName") || "").trim();
      if (!detailName) return;
      if (detailType === "FUNCTION") names.functionNames.push(detailName);
      if (detailType === "TYPE") names.typeNames.push(detailName);
      if (detailType === "INDEX") names.indexNames.push(detailName);
    });
  return names;
};

const mapDatabaseRowForForm = (item, details) => {
  const id = getValue(
    item,
    "applicationVersionDatabaseObjectId",
    "ApplicationVersionDatabaseObjectId",
  );
  const schema = getSchemaSelection(getValue(item, "schemaName", "SchemaName"));
  return {
    rowId: createRowId("database-object-edit"),
    ...schema,
    objectName: String(getValue(item, "objectName", "ObjectName") || ""),
    description: String(getValue(item, "description", "Description") || ""),
    ...mapDetailNames(details, id),
  };
};

const mapMigrationObjectForForm = (item, details) => {
  const id = getValue(
    item,
    "applicationVersionDataMigrationObjectId",
    "ApplicationVersionDataMigrationObjectId",
  );
  const schema = getSchemaSelection(getValue(item, "schemaName", "SchemaName"));
  return {
    objectId: createRowId("migration-object-edit"),
    ...schema,
    objectName: String(getValue(item, "objectName", "ObjectName") || ""),
    description: String(getValue(item, "description", "Description") || ""),
    ...mapDetailNames(details, id),
  };
};

const buildEditFormValues = (detailsResponse) => {
  const payload = getApiData(detailsResponse);
  const version =
    getValue(payload, "version", "Version") ??
    getValue(payload, "applicationVersion", "ApplicationVersion") ??
    {};
  const participants = getCollection(payload, "participants", "Participants");
  const features = getCollection(payload, "features", "Features");
  const databaseObjects = getCollection(
    payload,
    "databaseObjects",
    "DatabaseObjects",
  );
  const databaseObjectDetails = getCollection(
    payload,
    "databaseObjectDetails",
    "DatabaseObjectDetails",
  );
  const migrationObjects = getCollection(
    payload,
    "migrationObjects",
    "MigrationObjects",
  );
  const migrationObjectDetails = getCollection(
    payload,
    "migrationObjectDetails",
    "MigrationObjectDetails",
  );
  const checklistItems = getCollection(
    payload,
    "checklistItems",
    "ChecklistItems",
  );

  const databaseBuckets = {
    databaseTablesAdded: [],
    databaseTablesModified: [],
    databaseStoredProceduresAdded: [],
    databaseStoredProceduresModified: [],
  };

  databaseObjects.forEach((item) => {
    const objectType = String(getValue(item, "objectType", "ObjectType") || "").toUpperCase();
    const changeType = String(getValue(item, "changeType", "ChangeType") || "").toUpperCase();
    const row = mapDatabaseRowForForm(item, databaseObjectDetails);
    if (objectType === "TABLE" && changeType === "ADDED") databaseBuckets.databaseTablesAdded.push(row);
    if (objectType === "TABLE" && changeType === "MODIFIED") databaseBuckets.databaseTablesModified.push(row);
    if (objectType === "STORED_PROCEDURE" && changeType === "ADDED") {
      databaseBuckets.databaseStoredProceduresAdded.push(row);
    }
    if (objectType === "STORED_PROCEDURE" && changeType === "MODIFIED") {
      databaseBuckets.databaseStoredProceduresModified.push(row);
    }
  });

  const migrationGroups = new Map();
  migrationObjects.forEach((item) => {
    const groupId = String(getValue(item, "mappingGroupId", "MappingGroupId") || "");
    const objectType = String(getValue(item, "objectType", "ObjectType") || "").toUpperCase();
    const key = `${objectType}-${groupId}`;
    if (!migrationGroups.has(key)) {
      migrationGroups.set(key, {
        rowId: createRowId("migration-mapping-edit"),
        objectType,
        mappingNumber: Number(getValue(item, "mappingNumber", "MappingNumber") || 0),
        sourceObjects: [],
        targetObjects: [],
      });
    }
    const group = migrationGroups.get(key);
    const side = String(getValue(item, "migrationSide", "MigrationSide") || "").toUpperCase();
    const object = mapMigrationObjectForForm(item, migrationObjectDetails);
    if (side === "SOURCE") group.sourceObjects.push(object);
    if (side === "TARGET") group.targetObjects.push(object);
  });

  const allMigrationGroups = Array.from(migrationGroups.values()).sort(
    (first, second) => first.mappingNumber - second.mappingNumber,
  );
  const firstMigration = migrationObjects[0] || {};
  const sourceDatabase = getDatabaseSelection(
    getValue(firstMigration, "sourceDatabaseName", "SourceDatabaseName"),
  );
  const targetDatabase = getDatabaseSelection(
    getValue(firstMigration, "targetDatabaseName", "TargetDatabaseName"),
  );

  return {
    values: {
      ...EMPTY_FORM_VALUES,
      applicationId: String(getValue(version, "applicationId", "ApplicationId") || ""),
      versionNumber: String(getValue(version, "versionNumber", "VersionNumber") || ""),
      releaseName: String(getValue(version, "releaseName", "ReleaseName") || ""),
      releaseType: String(getValue(version, "releaseType", "ReleaseType") || ""),
      status: String(getValue(version, "status", "Status") || "Planning"),
      buildNumber: String(getValue(version, "buildNumber", "BuildNumber") || ""),
      environment: String(getValue(version, "environment", "Environment") || "Development"),
      deploymentDate: toDateInputValue(
        getValue(
          version,
          "deploymentDateTime",
          "DeploymentDateTime",
          "deploymentDate",
          "DeploymentDate",
          "deploymentDateAndTime",
          "DeploymentDateAndTime",
        ),
      ),
      developedBy: getParticipantIdsByRole(
        participants,
        "DEVELOPER",
      ),

      testedBy: getParticipantIdsByRole(
        participants,
        "TESTER",
      ),
      approvedBy: (() => {
        const participantApproverIds =
          getParticipantIdsByRole(
            participants,
            "APPROVER",
          );

        if (participantApproverIds.length) {
          return participantApproverIds;
        }

        return uniqueStringIds([
          getValue(
            version,
            "approvedBy",
            "ApprovedBy",
            "approvedByPersonId",
            "ApprovedByPersonId",
          ),
        ]);
      })(),
      teamLeadId: String(getValue(version, "teamLeadId", "TeamLeadId", "teamLeadPersonId", "TeamLeadPersonId") || ""),
      gitBranch: String(getValue(version, "gitBranch", "GitBranch") || ""),
      versionDescription: String(getValue(version, "versionDescription", "VersionDescription") || ""),
      features: features.map((feature) => ({
        rowId: createRowId("feature-edit"),
        moduleId: String(getValue(feature, "moduleId", "ModuleId") || ""),
        moduleName: String(getValue(feature, "moduleName", "ModuleName") || ""),
        subModuleId: String(getValue(feature, "subModuleId", "SubModuleId") || ""),
        subModuleName: String(getValue(feature, "subModuleName", "SubModuleName") || ""),
        feature: String(getValue(feature, "feature", "Feature", "featureDescription", "FeatureDescription") || ""),
      })),
      ...databaseBuckets,
      dataMigration: migrationObjects.length > 0,
      dataMigrationSourceDatabaseOption: sourceDatabase.option,
      dataMigrationSourceCustomDatabaseName: sourceDatabase.customName,
      dataMigrationTargetDatabaseOption: targetDatabase.option,
      dataMigrationTargetCustomDatabaseName: targetDatabase.customName,
      dataMigrationTables: allMigrationGroups
        .filter((item) => item.objectType === "TABLE")
        .map(({ objectType, mappingNumber, ...mapping }) => mapping),
      dataMigrationStoredProcedures: allMigrationGroups
        .filter((item) => item.objectType === "STORED_PROCEDURE")
        .map(({ objectType, mappingNumber, ...mapping }) => mapping),
      dataMigrationDescription: String(
        migrationObjects
          .map((item) => getValue(item, "migrationDescription", "MigrationDescription"))
          .find(Boolean) || "",
      ),
      deploymentChecklist: checklistItems.map((item) =>
        String(getValue(item, "checklistKey", "ChecklistKey") || ""),
      ),
      remarks: String(getValue(version, "remarks", "Remarks") || ""),
    },
    applicationVersionId: Number(
      getValue(version, "applicationVersionId", "ApplicationVersionId") || 0,
    ),
    expectedRowVersion: getValue(version, "rowVersion", "RowVersion") || null,
    payload,
  };
};

const DATABASE_SCHEMA_OPTIONS = [
  { value: "emp", label: "emp" },
  { value: "dbo", label: "dbo" },
  { value: "document", label: "document" },
  { value: "master", label: "master" },
  { value: "OTHER", label: "Other" },
];

const DATABASE_OPTIONS = [
  { value: "SSWhite_HR", label: "SSWhite_HR" },
  { value: "ITEIndia", label: "ITEIndia" },
  { value: "Surgical_Implant", label: "Surgical_Implant" },
  { value: "Pluto", label: "Pluto" },
  { value: "MasterSheet_PowerBi", label: "MasterSheet_PowerBi" },
  { value: "OTHER", label: "Other" },
];

const resolveMigrationDatabaseName = (option, customName) =>
  option === "OTHER"
    ? String(customName || "").trim()
    : String(option || "").trim();

const createRowId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const normalizeNameList = (values = []) => {
  const rawValues = Array.isArray(values)
    ? values
    : String(values || "").split(/[\n,;]+/);

  return Array.from(
    new Set(
      rawValues
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );
};

const toNullableNameList = (values = []) => {
  const names = normalizeNameList(values);
  return names.length > 0 ? names : null;
};

const createDatabaseChangeRow = () => ({
  rowId: createRowId("database-object"),
  schemaOption: "",
  customSchemaName: "",
  objectName: "",
  description: "",
  functionNames: [],
  typeNames: [],
  indexNames: [],
});

const resolveDatabaseSchemaName = (row) =>
  row.schemaOption === "OTHER"
    ? String(row.customSchemaName || "").trim()
    : String(row.schemaOption || "").trim();

const prepareDatabaseRows = (rows = [], includeIndexes = false) =>
  rows.map((row) => ({
    schemaName: resolveDatabaseSchemaName(row),
    objectName: String(row.objectName || "").trim(),
    description: String(row.description || "").trim(),
    functionNames: toNullableNameList(row.functionNames),
    typeNames: toNullableNameList(row.typeNames),
    ...(includeIndexes
      ? { indexNames: toNullableNameList(row.indexNames) }
      : {}),
  }));

const createMigrationSideObject = () => ({
  objectId: createRowId("migration-object"),
  schemaOption: "",
  customSchemaName: "",
  objectName: "",
  description: "",
  functionNames: [],
  typeNames: [],
  indexNames: [],
});

const createMigrationMappingRow = () => ({
  rowId: createRowId("migration-mapping"),
  sourceObjects: [createMigrationSideObject()],
  targetObjects: [createMigrationSideObject()],
});

const resolveMigrationSchemaName = (schemaOption, customSchemaName) =>
  schemaOption === "OTHER"
    ? String(customSchemaName || "").trim()
    : String(schemaOption || "").trim();

const prepareMigrationSideObject = (object, includeIndexes = false) => ({
  schemaName: resolveMigrationSchemaName(
    object.schemaOption,
    object.customSchemaName,
  ),
  objectName: String(object.objectName || "").trim(),
  description: String(object.description || "").trim(),
  functionNames: toNullableNameList(object.functionNames),
  typeNames: toNullableNameList(object.typeNames),
  ...(includeIndexes
    ? { indexNames: toNullableNameList(object.indexNames) }
    : {}),
});

const getMigrationRelationType = (sourceCount, targetCount) => {
  if (sourceCount === 1 && targetCount === 1) return "ONE_TO_ONE";
  if (sourceCount === 1 && targetCount > 1) return "ONE_TO_MANY";
  if (sourceCount > 1 && targetCount === 1) return "MANY_TO_ONE";
  return "MANY_TO_MANY";
};

const getMigrationRelationLabel = (sourceCount, targetCount) => {
  if (sourceCount === 1 && targetCount === 1) return "1 old → 1 new";
  if (sourceCount === 1 && targetCount > 1) {
    return `Split: 1 old → ${targetCount} new`;
  }
  if (sourceCount > 1 && targetCount === 1) {
    return `Merge: ${sourceCount} old → 1 new`;
  }
  return `${sourceCount} old → ${targetCount} new`;
};

const prepareMigrationMappings = (
  rows = [],
  sourceDatabase = "",
  targetDatabase = "",
  includeIndexes = false,
) =>
  rows.map((row, mappingIndex) => ({
    mappingNumber: mappingIndex + 1,
    sourceDatabase,
    targetDatabase,
    relationType: getMigrationRelationType(
      row.sourceObjects?.length || 0,
      row.targetObjects?.length || 0,
    ),
    sourceObjects: (row.sourceObjects || []).map((object) =>
      prepareMigrationSideObject(object, includeIndexes),
    ),
    targetObjects: (row.targetObjects || []).map((object) =>
      prepareMigrationSideObject(object, includeIndexes),
    ),
  }));

const getQualifiedMigrationObjectName = (databaseName, objectDetails) =>
  `[${databaseName}].[${objectDetails.schemaName}].[${objectDetails.objectName}]`;

const appendRelatedNamesToSummary = (lines, object, indent = "    ") => {
  if (object.functionNames?.length) {
    lines.push(`${indent}Functions: ${object.functionNames.join(", ")}`);
  }

  if (object.typeNames?.length) {
    lines.push(`${indent}Types: ${object.typeNames.join(", ")}`);
  }

  if (object.indexNames?.length) {
    lines.push(`${indent}Indexes: ${object.indexNames.join(", ")}`);
  }
};

const buildDatabaseDescription = ({
  tableAdded,
  tableModified,
  spAdded,
  spModified,
  dataMigration,
  dataMigrationSourceDatabase,
  dataMigrationTargetDatabase,
  dataMigrationTableDetails,
  dataMigrationStoredProcedureDetails,
  dataMigrationDescription,
}) => {
  const sections = [
    ["Table Added", tableAdded],
    ["Table Modified", tableModified],
    ["SP Added", spAdded],
    ["SP Modified", spModified],
  ];

  const lines = [];

  sections.forEach(([heading, rows]) => {
    if (!rows.length) return;

    lines.push(`${heading}:`);

    rows.forEach((row) => {
      lines.push(
        `- [${row.schemaName}].[${row.objectName}]: ${row.description}`,
      );
      appendRelatedNamesToSummary(lines, row);
    });
  });

  if (dataMigration) {
    lines.push(
      "Data Migration:",
      `- From Database: ${dataMigrationSourceDatabase}`,
      `- To Database: ${dataMigrationTargetDatabase}`,
    );

    const appendMappings = (heading, mappings) => {
      if (!mappings.length) return;

      lines.push(`- ${heading}:`);

      mappings.forEach((mapping) => {
        lines.push(
          `  Mapping #${mapping.mappingNumber} (${getMigrationRelationLabel(
            mapping.sourceObjects.length,
            mapping.targetObjects.length,
          )})`,
          "    Old / Source Objects:",
        );

        mapping.sourceObjects.forEach((object) => {
          lines.push(
            `      • ${getQualifiedMigrationObjectName(
              mapping.sourceDatabase,
              object,
            )}: ${object.description}`,
          );
          appendRelatedNamesToSummary(lines, object, "        ");
        });

        lines.push("    New / Target Objects:");

        mapping.targetObjects.forEach((object) => {
          lines.push(
            `      • ${getQualifiedMigrationObjectName(
              mapping.targetDatabase,
              object,
            )}: ${object.description}`,
          );
          appendRelatedNamesToSummary(lines, object, "        ");
        });
      });
    };

    appendMappings("Table Mappings", dataMigrationTableDetails);
    appendMappings(
      "Stored Procedure Mappings",
      dataMigrationStoredProcedureDetails,
    );

    lines.push(`- Migration Plan: ${dataMigrationDescription.trim()}`);
  }

  return lines.join("\n");
};

const sqlNameListSchema = Yup.array()
  .of(
    Yup.string()
      .trim()
      .max(256, "Each name cannot exceed 256 characters"),
  )
  .nullable()
  .notRequired();

const databaseObjectRowSchema = Yup.object({
  schemaOption: Yup.string().required("Schema is required"),
  customSchemaName: Yup.string()
    .trim()
    .max(128, "Schema cannot exceed 128 characters")
    .test(
      "custom-schema-required",
      "Custom schema is required",
      function validateCustomSchema(value) {
        return this.parent.schemaOption !== "OTHER" || Boolean(value?.trim());
      },
    )
    .matches(/^[A-Za-z_][A-Za-z0-9_]*$/, {
      message:
        "Use letters, numbers and underscores only; do not start with a number",
      excludeEmptyString: true,
    }),
  objectName: Yup.string()
    .trim()
    .max(256, "Name cannot exceed 256 characters")
    .required("Name is required")
    .test(
      "name-without-schema",
      "Enter only the object name; select schema separately",
      (value) => !value || !value.includes("."),
    ),
  description: Yup.string()
    .trim()
    .max(1000, "Description cannot exceed 1000 characters")
    .required("Description is required"),
  functionNames: sqlNameListSchema,
  typeNames: sqlNameListSchema,
  indexNames: sqlNameListSchema,
});

const migrationSideObjectSchema = Yup.object({
  schemaOption: Yup.string().required("Schema is required"),
  customSchemaName: Yup.string()
    .trim()
    .max(128, "Schema cannot exceed 128 characters")
    .test(
      "migration-custom-schema-required",
      "Custom schema is required",
      function validateMigrationCustomSchema(value) {
        return this.parent.schemaOption !== "OTHER" || Boolean(value?.trim());
      },
    )
    .matches(/^[A-Za-z_][A-Za-z0-9_]*$/, {
      message:
        "Use letters, numbers and underscores only; do not start with a number",
      excludeEmptyString: true,
    }),
  objectName: Yup.string()
    .trim()
    .max(256, "Name cannot exceed 256 characters")
    .required("Object name is required")
    .test(
      "migration-name-without-schema",
      "Enter only the object name; select schema separately",
      (value) => !value || !value.includes("."),
    ),
  description: Yup.string()
    .trim()
    .max(1000, "Description cannot exceed 1000 characters")
    .required("Description is required"),
  functionNames: sqlNameListSchema,
  typeNames: sqlNameListSchema,
  indexNames: sqlNameListSchema,
});

const migrationMappingSchema = Yup.object({
  sourceObjects: Yup.array()
    .of(migrationSideObjectSchema)
    .min(1, "Add at least one old/source object"),
  targetObjects: Yup.array()
    .of(migrationSideObjectSchema)
    .min(1, "Add at least one new/target object"),
});

const normalizeDeploymentChecklistOptions = (records = []) =>
  records
    .map((item) => {
      const id =
        item?.deploymentChecklistId ??
        item?.deployment_Checklist_ID ??
        item?.deploymentChecklist_ID ??
        item?.checklistId ??
        item?.checklist_ID ??
        item?.id ??
        item?.value;

      const name =
        item?.deploymentChecklistName ??
        item?.deployment_Checklist_Name ??
        item?.checklistName ??
        item?.checklist_Name ??
        item?.title ??
        item?.name ??
        item?.label ??
        "";

      const description =
        item?.description ??
        item?.checklistDescription ??
        item?.deploymentChecklistDescription ??
        "";

      const isActive = item?.isActive ?? item?.IsActive ?? true;

      return {
        id: id === null || id === undefined ? "" : String(id),
        name: String(name || "").trim(),
        description: String(description || "").trim(),
        isActive: Boolean(isActive),
      };
    })
    .filter((item) => item.id && item.name && item.isActive);

const validationSchema = Yup.object({
  applicationId: Yup.string().required("Application is required"),
  versionNumber: Yup.string()
    .trim()
    .max(50, "Version number cannot exceed 50 characters")
    .required("Version number is required"),
  releaseName: Yup.string()
    .trim()
    .max(150, "Release name cannot exceed 150 characters")
    .required("Release name is required"),
  releaseType: Yup.string().required("Release type is required"),
  status: Yup.string().required("Status is required"),
  buildNumber: Yup.string()
    .trim()
    .max(50, "Build number cannot exceed 50 characters")
    .required("Build number is required"),
  environment: Yup.string().required("Environment is required"),
  deploymentDate: Yup.string()
    .required("Deployment date is required")
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      "Enter a valid deployment date",
    ),
  developedBy: Yup.array()
    .min(1, "Select at least one developer")
    .required("Developed By is required"),
  testedBy: Yup.array()
    .min(1, "Select at least one tester")
    .required("Tested By is required"),
  approvedBy: Yup.array()
    .of(Yup.string().trim().required())
    .min(1, "Select at least one approver")
    .required("Approved By is required"),
  teamLeadId: Yup.string().required(
    "Team Lead is not configured for the selected application",
  ),
  gitBranch: Yup.string()
    .trim()
    .max(250, "Git branch cannot exceed 250 characters")
    .required("Git branch is required"),
  versionDescription: Yup.string()
    .trim()
    .max(2000, "Version description cannot exceed 2000 characters")
    .required("Version description is required"),
  features: Yup.array().min(1, "Add at least one feature"),
  databaseTablesAdded: Yup.array().of(databaseObjectRowSchema),
  databaseTablesModified: Yup.array().of(databaseObjectRowSchema),
  databaseStoredProceduresAdded: Yup.array().of(databaseObjectRowSchema),
  databaseStoredProceduresModified: Yup.array().of(databaseObjectRowSchema),
  dataMigration: Yup.boolean(),
  dataMigrationSourceDatabaseOption: Yup.string().test(
    "source-database-required",
    "From Database is required",
    function validateSourceDatabase(value) {
      return !this.parent.dataMigration || Boolean(value);
    },
  ),
  dataMigrationSourceCustomDatabaseName: Yup.string()
    .trim()
    .max(128, "Database name cannot exceed 128 characters")
    .test(
      "source-custom-database-required",
      "Enter the source database name",
      function validateSourceCustomDatabase(value) {
        return (
          !this.parent.dataMigration ||
          this.parent.dataMigrationSourceDatabaseOption !== "OTHER" ||
          Boolean(value?.trim())
        );
      },
    ),
  dataMigrationTargetDatabaseOption: Yup.string()
    .test(
      "target-database-required",
      "To Database is required",
      function validateTargetDatabase(value) {
        return !this.parent.dataMigration || Boolean(value);
      },
    )
    .test(
      "different-target-database",
      "From Database and To Database must be different",
      function validateDifferentDatabase(value) {
        if (!this.parent.dataMigration || !value) return true;

        const sourceDatabase = resolveMigrationDatabaseName(
          this.parent.dataMigrationSourceDatabaseOption,
          this.parent.dataMigrationSourceCustomDatabaseName,
        ).toLowerCase();
        const targetDatabase = resolveMigrationDatabaseName(
          value,
          this.parent.dataMigrationTargetCustomDatabaseName,
        ).toLowerCase();

        return (
          !sourceDatabase ||
          !targetDatabase ||
          sourceDatabase !== targetDatabase
        );
      },
    ),
  dataMigrationTargetCustomDatabaseName: Yup.string()
    .trim()
    .max(128, "Database name cannot exceed 128 characters")
    .test(
      "target-custom-database-required",
      "Enter the target database name",
      function validateTargetCustomDatabase(value) {
        return (
          !this.parent.dataMigration ||
          this.parent.dataMigrationTargetDatabaseOption !== "OTHER" ||
          Boolean(value?.trim())
        );
      },
    ),
  dataMigrationTables: Yup.array()
    .of(migrationMappingSchema)
    .test(
      "migration-object-required",
      "Add at least one migrated table or stored procedure",
      function validateMigrationObjects(value) {
        return (
          !this.parent.dataMigration ||
          Boolean(value?.length) ||
          Boolean(this.parent.dataMigrationStoredProcedures?.length)
        );
      },
    ),
  dataMigrationStoredProcedures: Yup.array().of(migrationMappingSchema),
  dataMigrationDescription: Yup.string()
    .trim()
    .max(2000, "Data migration description cannot exceed 2000 characters")
    .test(
      "data-migration-description-required",
      "Describe the migration, execution order, validation, and rollback plan",
      function validateDataMigrationDescription(value) {
        return !this.parent.dataMigration || Boolean(value?.trim());
      },
    ),
  remarks: Yup.string()
    .trim()
    .max(2000, "Remarks cannot exceed 2000 characters"),
});

const unwrapRecords = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data?.result)) return response.data.result;
  if (Array.isArray(response?.data?.Result)) return response.data.Result;
  if (Array.isArray(response?.data?.records)) return response.data.records;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.Result)) return response.Result;
  if (Array.isArray(response?.records)) return response.records;
  return [];
};

const normalizeLookup = (records, idFields, nameFields) =>
  records
    .map((item) => {
      const id = idFields.reduce(
        (value, field) => value ?? item?.[field],
        undefined,
      );
      const name = nameFields.reduce(
        (value, field) => value ?? item?.[field],
        undefined,
      );

      return {
        id: id === undefined || id === null ? "" : String(id),
        name: name || "",
        raw: item,
      };
    })
    .filter((item) => item.id && item.name);

const normalizeModuleOptions = (records = []) =>
  records
    .map((item) => ({
      id: String(
        item?.Module_ID ??
        item?.module_ID ??
        item?.ModuleId ??
        item?.moduleId ??
        item?.Id ??
        item?.id ??
        item?.value ??
        "",
      ),
      applicationId: String(
        item?.Application_ID ??
        item?.application_ID ??
        item?.ApplicationId ??
        item?.applicationId ??
        "",
      ),
      name: String(
        item?.Module_Name ??
        item?.module_Name ??
        item?.ModuleName ??
        item?.moduleName ??
        item?.Name ??
        item?.name ??
        item?.label ??
        "",
      ).trim(),
      raw: item,
    }))
    .filter((item) => item.id && item.name);

const normalizeSubModuleOptions = (records = []) =>
  records
    .map((item) => ({
      id: String(
        item?.Sub_Module_ID ??
        item?.sub_Module_ID ??
        item?.SubModule_ID ??
        item?.subModule_ID ??
        item?.SubModuleId ??
        item?.subModuleId ??
        item?.Id ??
        item?.id ??
        item?.value ??
        "",
      ),
      applicationId: String(
        item?.Application_ID ??
        item?.application_ID ??
        item?.ApplicationId ??
        item?.applicationId ??
        "",
      ),
      moduleId: String(
        item?.Module_ID ??
        item?.module_ID ??
        item?.ModuleId ??
        item?.moduleId ??
        "",
      ),
      name: String(
        item?.Sub_Module_Name ??
        item?.sub_Module_Name ??
        item?.SubModule_Name ??
        item?.subModule_Name ??
        item?.SubModuleName ??
        item?.subModuleName ??
        item?.Name ??
        item?.name ??
        item?.label ??
        "",
      ).trim(),
      raw: item,
    }))
    .filter((item) => item.id && item.name);

const filterModulesByApplication = (options = [], applicationId) => {
  const normalizedApplicationId = String(applicationId || "");
  if (!normalizedApplicationId) return [];

  const hasApplicationReferences = options.some(
    (item) => String(item.applicationId || "") !== "",
  );

  return hasApplicationReferences
    ? options.filter(
      (item) => String(item.applicationId) === normalizedApplicationId,
    )
    : options;
};

const filterSubModulesByModule = (
  options = [],
  moduleId,
  applicationId = "",
) => {
  const normalizedModuleId = String(moduleId || "");
  const normalizedApplicationId = String(applicationId || "");
  if (!normalizedModuleId) return [];

  const hasModuleReferences = options.some(
    (item) => String(item.moduleId || "") !== "",
  );

  let filtered = hasModuleReferences
    ? options.filter((item) => String(item.moduleId) === normalizedModuleId)
    : options;

  const hasApplicationReferences = filtered.some(
    (item) => String(item.applicationId || "") !== "",
  );

  if (hasApplicationReferences && normalizedApplicationId) {
    filtered = filtered.filter(
      (item) => String(item.applicationId) === normalizedApplicationId,
    );
  }

  return filtered;
};

const toIdentifier = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  return /^\d+$/.test(String(value)) ? Number(value) : value;
};

const compactSelectProps = {
  displayEmpty: true,
  MenuProps: {
    PaperProps: {
      sx: {
        maxHeight: 320,
        "& .MuiMenuItem-root": {
          minHeight: 36,
          py: 0.75,
          fontSize: "0.875rem",
        },
      },
    },
  },
};

const compactFormSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
  },
  "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline):not(.MuiAutocomplete-inputRoot)":
  {
    height: 40,
  },
  "& .MuiAutocomplete-inputRoot": {
    minHeight: "40px !important",
    py: "2px !important",
  },
  "& .MuiOutlinedInput-input:not(textarea)": {
    py: "8.5px",
    fontSize: "0.875rem",
  },
  "& .MuiSelect-select": {
    minHeight: "0 !important",
    py: "8.5px !important",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiFormHelperText-root": {
    mt: 0.5,
    mx: 0.5,
  },
  "& .MuiInputBase-multiline textarea": {
    resize: "vertical !important",
    overflow: "auto !important",
  },
};

const RequiredMark = () => (
  <Box component="span" sx={{ color: "error.main" }}>
    *
  </Box>
);

const SectionCard = ({ icon, title, subtitle, children }) => (
  <Card
    sx={{
      mb: 2,
      p: 0,
      overflow: "hidden",
      border: "1px solid",
      borderColor: "divider",
      boxShadow: "none",
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      px={2}
      py={1.5}
      sx={{
        backgroundColor: "primary.light",
        color: "primary.main",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <FeatherIcon icon={icon} width="18" />
      <Box sx={{ ml: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} color="primary.main">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>

    <CardContent
      sx={{
        padding: { xs: "16px", sm: "14px 30px" },
        "&:last-child": { pb: { xs: 2, sm: "14px" } },
      }}
    >
      {children}
    </CardContent>
  </Card>
);

const NameListEditor = ({
  label,
  placeholder,
  values = [],
  onChange,
  helperText,
}) => {
  const [draft, setDraft] = useState("");
  const items = normalizeNameList(values);

  const addNames = () => {
    const newNames = normalizeNameList(draft);
    if (!newNames.length) return;

    onChange(normalizeNameList([...items, ...newNames]));
    setDraft("");
  };

  const removeName = (name) => {
    onChange(items.filter((item) => item !== name));
  };

  return (
    <Box>
      <CustomFormLabel sx={{ mt: 0 }}>{label}</CustomFormLabel>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <CustomTextField
          fullWidth
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addNames();
            }
          }}
          helperText={helperText || "Press Enter or click Add; comma-separated names are supported."}
          inputProps={{ maxLength: 1000 }}
        />
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={addNames}
          disabled={!draft.trim()}
          sx={{ minWidth: 68, minHeight: 40 }}
        >
          Add
        </Button>
      </Stack>

      {items.length > 0 && (
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 1 }}
        >
          {items.map((name) => (
            <Chip
              key={name}
              size="small"
              label={name}
              onDelete={() => removeName(name)}
              variant="outlined"
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const DatabaseObjectSection = ({
  title,
  subtitle,
  addButtonLabel,
  objectNameLabel,
  descriptionLabel,
  rows,
  fieldName,
  errors,
  touched,
  showIndexes = false,
  onAdd,
  onChange,
  onRemove,
  onBlur,
}) => {
  const rowErrors = Array.isArray(errors) ? errors : [];
  const rowTouched = Array.isArray(touched) ? touched : [];

  const hasError = (index, property) =>
    Boolean(rowTouched[index]?.[property] && rowErrors[index]?.[property]);

  const getHelperText = (index, property) =>
    rowTouched[index]?.[property] ? rowErrors[index]?.[property] || "" : "";

  return (
    <Card
      sx={{
        mb: 2,
        p: 0,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: "action.hover",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography fontWeight={700} color="primary.main">
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          <Button
            type="button"
            size="small"
            variant="outlined"
            startIcon={<FeatherIcon icon="plus" size="16" />}
            onClick={onAdd}
            sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
          >
            {addButtonLabel}
          </Button>
        </Stack>
      </Box>

      <CardContent
        sx={{
          p: 1.5,
          "&:last-child": { pb: 1.5 },
          maxHeight: rows.length > 0 ? { xs: 460, sm: 520, md: 580 } : "none",
          overflowY: rows.length > 0 ? "auto" : "visible",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-track": {
            bgcolor: "action.hover",
            borderRadius: 10,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "action.disabled",
            borderRadius: 10,
            border: "2px solid transparent",
            backgroundClip: "padding-box",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(120, 120, 120, 0.45) transparent",
        }}
      >
        {rows.length === 0 ? (
          <Box
            sx={{
              py: 2.5,
              textAlign: "center",
              color: "text.secondary",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              No records added. Click {addButtonLabel} to enter details.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {rows.map((row, index) => {
              const usesCustomSchema = row.schemaOption === "OTHER";

              return (
                <Paper
                  key={row.rowId}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 1, bgcolor: "background.paper" }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      Record #{index + 1}
                    </Typography>
                    <Tooltip title="Remove row">
                      <IconButton
                        type="button"
                        size="small"
                        color="error"
                        onClick={() => onRemove(index)}
                        aria-label={`Remove ${title} row ${index + 1}`}
                      >
                        <FeatherIcon icon="trash-2" size="17" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Grid container spacing={1.5} alignItems="flex-start">
                    <Grid item xs={12} sm={6} md={usesCustomSchema ? 2 : 3}>
                      <CustomFormLabel
                        htmlFor={`${fieldName}-${index}-schemaOption`}
                        sx={{ mt: 0 }}
                      >
                        Schema <RequiredMark />
                      </CustomFormLabel>
                      <CustomTextField
                        id={`${fieldName}-${index}-schemaOption`}
                        name={`${fieldName}[${index}].schemaOption`}
                        select
                        fullWidth
                        value={row.schemaOption}
                        onChange={(event) =>
                          onChange(index, "schemaOption", event.target.value)
                        }
                        onBlur={onBlur}
                        error={hasError(index, "schemaOption")}
                        helperText={getHelperText(index, "schemaOption")}
                        SelectProps={compactSelectProps}
                      >
                        <MenuItem value="" disabled>
                          Select schema
                        </MenuItem>
                        {DATABASE_SCHEMA_OPTIONS.map((schema) => (
                          <MenuItem key={schema.value} value={schema.value}>
                            {schema.label}
                          </MenuItem>
                        ))}
                      </CustomTextField>
                    </Grid>

                    {usesCustomSchema && (
                      <Grid item xs={12} sm={6} md={2}>
                        <CustomFormLabel
                          htmlFor={`${fieldName}-${index}-customSchemaName`}
                          sx={{ mt: 0 }}
                        >
                          Custom Schema <RequiredMark />
                        </CustomFormLabel>
                        <CustomTextField
                          id={`${fieldName}-${index}-customSchemaName`}
                          name={`${fieldName}[${index}].customSchemaName`}
                          fullWidth
                          placeholder="Example: audit"
                          value={row.customSchemaName}
                          onChange={(event) =>
                            onChange(
                              index,
                              "customSchemaName",
                              event.target.value,
                            )
                          }
                          onBlur={onBlur}
                          error={hasError(index, "customSchemaName")}
                          helperText={getHelperText(index, "customSchemaName")}
                          inputProps={{ maxLength: 128 }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={usesCustomSchema ? 3 : 3}>
                      <CustomFormLabel
                        htmlFor={`${fieldName}-${index}-objectName`}
                        sx={{ mt: 0 }}
                      >
                        {objectNameLabel} <RequiredMark />
                      </CustomFormLabel>
                      <CustomTextField
                        id={`${fieldName}-${index}-objectName`}
                        name={`${fieldName}[${index}].objectName`}
                        fullWidth
                        placeholder="Enter name without schema"
                        value={row.objectName}
                        onChange={(event) =>
                          onChange(index, "objectName", event.target.value)
                        }
                        onBlur={onBlur}
                        error={hasError(index, "objectName")}
                        helperText={getHelperText(index, "objectName")}
                        inputProps={{ maxLength: 256 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={usesCustomSchema ? 5 : 6}>
                      <CustomFormLabel
                        htmlFor={`${fieldName}-${index}-description`}
                        sx={{ mt: 0 }}
                      >
                        {descriptionLabel} <RequiredMark />
                      </CustomFormLabel>
                      <CustomTextField
                        id={`${fieldName}-${index}-description`}
                        name={`${fieldName}[${index}].description`}
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder={descriptionLabel}
                        value={row.description}
                        onChange={(event) =>
                          onChange(index, "description", event.target.value)
                        }
                        onBlur={onBlur}
                        error={hasError(index, "description")}
                        helperText={getHelperText(index, "description")}
                        inputProps={{ maxLength: 1000 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={showIndexes ? 4 : 6}>
                      <NameListEditor
                        label="Function Names (Optional)"
                        placeholder="Example: fn_GetEmployeeAge"
                        values={row.functionNames}
                        onChange={(names) =>
                          onChange(index, "functionNames", names)
                        }
                        helperText="Optional. Add function names only when this object uses them."
                      />
                    </Grid>

                    <Grid item xs={12} md={showIndexes ? 4 : 6}>
                      <NameListEditor
                        label="Type Names (Optional)"
                        placeholder="Example: EmployeeTableType"
                        values={row.typeNames}
                        onChange={(names) => onChange(index, "typeNames", names)}
                        helperText="Optional. Add SQL/user-defined type names only when applicable."
                      />
                    </Grid>

                    {showIndexes && (
                      <Grid item xs={12} md={4}>
                        <NameListEditor
                          label="Index Names (Optional)"
                          placeholder="Example: IX_Employee_DepartmentId"
                          values={row.indexNames}
                          onChange={(names) =>
                            onChange(index, "indexNames", names)
                          }
                          helperText="Optional. Add indexes defined on this table. Names only."
                        />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const MigrationMappingSection = ({
  title,
  subtitle,
  addButtonLabel,
  objectLabel,
  rows,
  fieldName,
  errors,
  touched,
  sourceDatabaseName,
  targetDatabaseName,
  showIndexes = false,
  onAddMapping,
  onRemoveMapping,
  onAddObject,
  onRemoveObject,
  onObjectChange,
  onBlur,
}) => {
  const mappingErrors = Array.isArray(errors) ? errors : [];
  const mappingTouched = Array.isArray(touched) ? touched : [];
  const canAdd = Boolean(sourceDatabaseName && targetDatabaseName);

  const getObjectError = (mappingIndex, side, objectIndex, property) =>
    mappingErrors[mappingIndex]?.[side]?.[objectIndex]?.[property];

  const isObjectTouched = (mappingIndex, side, objectIndex, property) =>
    mappingTouched[mappingIndex]?.[side]?.[objectIndex]?.[property];

  const renderObjectCard = ({
    mapping,
    mappingIndex,
    side,
    object,
    objectIndex,
    databaseName,
  }) => {
    const objects = mapping[side] || [];
    const sideLabel = side === "sourceObjects" ? "Old" : "New";
    const usesCustomSchema = object.schemaOption === "OTHER";

    const hasError = (property) =>
      Boolean(
        isObjectTouched(mappingIndex, side, objectIndex, property) &&
        getObjectError(mappingIndex, side, objectIndex, property),
      );

    const helperText = (property) =>
      isObjectTouched(mappingIndex, side, objectIndex, property)
        ? getObjectError(mappingIndex, side, objectIndex, property) || ""
        : "";

    return (
      <Paper
        key={object.objectId}
        variant="outlined"
        sx={{ p: 1.25, borderRadius: 1, bgcolor: "background.paper" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              {sideLabel} {objectLabel} #{objectIndex + 1}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {databaseName}
            </Typography>
          </Box>

          <Tooltip
            title={
              objects.length <= 1
                ? `At least one ${sideLabel.toLowerCase()} object is required`
                : "Remove object"
            }
          >
            <span>
              <IconButton
                type="button"
                size="small"
                color="error"
                disabled={objects.length <= 1}
                onClick={() =>
                  onRemoveObject(mappingIndex, side, objectIndex)
                }
              >
                <FeatherIcon icon="trash-2" size="16" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Grid container spacing={1.25} alignItems="flex-start">
          <Grid item xs={12} sm={usesCustomSchema ? 4 : 5}>
            <CustomFormLabel sx={{ mt: 0 }}>
              Schema <RequiredMark />
            </CustomFormLabel>
            <CustomTextField
              name={`${fieldName}[${mappingIndex}].${side}[${objectIndex}].schemaOption`}
              select
              fullWidth
              value={object.schemaOption}
              onChange={(event) =>
                onObjectChange(
                  mappingIndex,
                  side,
                  objectIndex,
                  "schemaOption",
                  event.target.value,
                )
              }
              onBlur={onBlur}
              error={hasError("schemaOption")}
              helperText={helperText("schemaOption")}
              SelectProps={compactSelectProps}
            >
              <MenuItem value="" disabled>
                Select schema
              </MenuItem>
              {DATABASE_SCHEMA_OPTIONS.map((schema) => (
                <MenuItem key={schema.value} value={schema.value}>
                  {schema.label}
                </MenuItem>
              ))}
            </CustomTextField>
          </Grid>

          {usesCustomSchema && (
            <Grid item xs={12} sm={4}>
              <CustomFormLabel sx={{ mt: 0 }}>
                Custom Schema <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                name={`${fieldName}[${mappingIndex}].${side}[${objectIndex}].customSchemaName`}
                fullWidth
                placeholder="Example: audit"
                value={object.customSchemaName}
                onChange={(event) =>
                  onObjectChange(
                    mappingIndex,
                    side,
                    objectIndex,
                    "customSchemaName",
                    event.target.value,
                  )
                }
                onBlur={onBlur}
                error={hasError("customSchemaName")}
                helperText={helperText("customSchemaName")}
                inputProps={{ maxLength: 128 }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={usesCustomSchema ? 4 : 7}>
            <CustomFormLabel sx={{ mt: 0 }}>
              {sideLabel} {objectLabel} Name <RequiredMark />
            </CustomFormLabel>
            <CustomTextField
              name={`${fieldName}[${mappingIndex}].${side}[${objectIndex}].objectName`}
              fullWidth
              placeholder={`Enter ${objectLabel.toLowerCase()} name`}
              value={object.objectName}
              onChange={(event) =>
                onObjectChange(
                  mappingIndex,
                  side,
                  objectIndex,
                  "objectName",
                  event.target.value,
                )
              }
              onBlur={onBlur}
              error={hasError("objectName")}
              helperText={helperText("objectName")}
              inputProps={{ maxLength: 256 }}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomFormLabel sx={{ mt: 0 }}>
              {sideLabel} Description / Use <RequiredMark />
            </CustomFormLabel>
            <CustomTextField
              name={`${fieldName}[${mappingIndex}].${side}[${objectIndex}].description`}
              fullWidth
              multiline
              minRows={2}
              placeholder={`Describe the ${sideLabel.toLowerCase()} ${objectLabel.toLowerCase()} purpose or use`}
              value={object.description}
              onChange={(event) =>
                onObjectChange(
                  mappingIndex,
                  side,
                  objectIndex,
                  "description",
                  event.target.value,
                )
              }
              onBlur={onBlur}
              error={hasError("description")}
              helperText={helperText("description")}
              inputProps={{ maxLength: 1000 }}
            />
          </Grid>

          <Grid item xs={12} md={showIndexes ? 4 : 6}>
            <NameListEditor
              label="Function Names (Optional)"
              placeholder="Example: fn_GetEmployeeAge"
              values={object.functionNames}
              onChange={(names) =>
                onObjectChange(
                  mappingIndex,
                  side,
                  objectIndex,
                  "functionNames",
                  names,
                )
              }
              helperText="Optional. Add function names only when applicable."
            />
          </Grid>

          <Grid item xs={12} md={showIndexes ? 4 : 6}>
            <NameListEditor
              label="Type Names (Optional)"
              placeholder="Example: EmployeeTableType"
              values={object.typeNames}
              onChange={(names) =>
                onObjectChange(
                  mappingIndex,
                  side,
                  objectIndex,
                  "typeNames",
                  names,
                )
              }
              helperText="Optional. Add SQL/user-defined type names only when applicable."
            />
          </Grid>

          {showIndexes && (
            <Grid item xs={12} md={4}>
              <NameListEditor
                label="Index Names (Optional)"
                placeholder="Example: IX_Employee_DepartmentId"
                values={object.indexNames}
                onChange={(names) =>
                  onObjectChange(
                    mappingIndex,
                    side,
                    objectIndex,
                    "indexNames",
                    names,
                  )
                }
                helperText={`Optional. Add indexes defined on this ${sideLabel.toLowerCase()} table. Names only.`}
              />
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const renderSide = ({ mapping, mappingIndex, side, heading, databaseName }) => {
    const objects = mapping[side] || [];
    const addLabel = side === "sourceObjects" ? `Add Old ${objectLabel}` : `Add New ${objectLabel}`;

    return (
      <Grid item xs={12} lg={6}>
        <Paper
          variant="outlined"
          sx={{
            height: "100%",
            p: 1.25,
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1.25 }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {heading}
              </Typography>
              <Chip
                size="small"
                label={databaseName || "Select database"}
                sx={{ mt: 0.5 }}
              />
            </Box>
            <Button
              type="button"
              size="small"
              variant="outlined"
              startIcon={<FeatherIcon icon="plus" size="15" />}
              onClick={() => onAddObject(mappingIndex, side)}
            >
              {addLabel}
            </Button>
          </Stack>

          <Stack spacing={1.25}>
            {objects.map((object, objectIndex) =>
              renderObjectCard({
                mapping,
                mappingIndex,
                side,
                object,
                objectIndex,
                databaseName,
              }),
            )}
          </Stack>
        </Paper>
      </Grid>
    );
  };

  return (
    <Card
      sx={{
        mt: 2,
        p: 0,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: "action.hover",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography fontWeight={700} color="primary.main">
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          <Tooltip
            title={
              canAdd
                ? ""
                : "Select both From Database and To Database first"
            }
          >
            <span>
              <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={<FeatherIcon icon="plus" size="16" />}
                onClick={onAddMapping}
                disabled={!canAdd}
              >
                {addButtonLabel}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <CardContent
        sx={{
          p: 1.5,
          "&:last-child": { pb: 1.5 },
          maxHeight: rows.length > 0 ? { xs: 620, md: 760 } : "none",
          overflowY: rows.length > 0 ? "auto" : "visible",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-track": {
            bgcolor: "action.hover",
            borderRadius: 10,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "action.disabled",
            borderRadius: 10,
            border: "2px solid transparent",
            backgroundClip: "padding-box",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(120, 120, 120, 0.45) transparent",
        }}
      >
        {rows.length === 0 ? (
          <Box
            sx={{
              py: 2.5,
              textAlign: "center",
              color: "text.secondary",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              {canAdd
                ? `No ${objectLabel.toLowerCase()} migration mappings added.`
                : "Select source and target databases first."}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {rows.map((mapping, mappingIndex) => {
              const sourceCount = mapping.sourceObjects?.length || 0;
              const targetCount = mapping.targetObjects?.length || 0;

              return (
                <Paper
                  key={mapping.rowId}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 1, bgcolor: "background.paper" }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 1.25 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={700} color="primary.main">
                        {objectLabel} Mapping #{mappingIndex + 1}
                      </Typography>
                      <Chip
                        size="small"
                        label={getMigrationRelationLabel(
                          sourceCount,
                          targetCount,
                        )}
                        variant="outlined"
                      />
                    </Stack>

                    <Tooltip title="Remove complete mapping">
                      <IconButton
                        type="button"
                        size="small"
                        color="error"
                        onClick={() => onRemoveMapping(mappingIndex)}
                      >
                        <FeatherIcon icon="trash-2" size="17" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
                    Add one or more old objects and one or more new objects. This
                    supports splitting one object into many, merging many into
                    one, and many-to-many restructuring.
                  </Alert>

                  <Grid container spacing={1.5} alignItems="stretch">
                    {renderSide({
                      mapping,
                      mappingIndex,
                      side: "sourceObjects",
                      heading: "Old / Source Database Objects",
                      databaseName: sourceDatabaseName,
                    })}
                    {renderSide({
                      mapping,
                      mappingIndex,
                      side: "targetObjects",
                      heading: "New / Target Database Objects",
                      databaseName: targetDatabaseName,
                    })}
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const VersionForm = ({
  onSave = AddOrEditApplicationVersion,
  loadModules,
  loadSubModules,
  loadVersions = GetApplicationVersionList,
  loadVersionById = GetApplicationVersionById,
  deleteVersion = DeleteApplicationVersion,
  initialVersions = EMPTY_ARRAY,
}) => {
  const [applications, setApplications] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modules, setModules] = useState([]);
  const [subModules, setSubModules] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [allSubModules, setAllSubModules] = useState([]);
  const [isLoadingApplications, setIsLoadingApplications] = useState(true);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [isLoadingSubModules, setIsLoadingSubModules] = useState(false);
  const deploymentChecklistOptions = DEPLOYMENT_CHECKLIST_OPTIONS;
  const [loadError, setLoadError] = useState("");

  const [featureDraft, setFeatureDraft] = useState({
    moduleId: "",
    subModuleId: "",
    feature: "",
  });
  const [featureError, setFeatureError] = useState("");

  const [versions, setVersions] = useState([]);
  const [totalVersions, setTotalVersions] = useState(0);
  const [versionPage, setVersionPage] = useState(0);
  const [versionRowsPerPage, setVersionRowsPerPage] = useState(5);
  const [versionSearchText, setVersionSearchText] = useState("");
  const [appliedVersionSearch, setAppliedVersionSearch] = useState("");
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [versionRefreshKey, setVersionRefreshKey] = useState(0);
  const [editingVersionId, setEditingVersionId] = useState(0);
  const [expectedRowVersion, setExpectedRowVersion] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isLoadingVersionDetails, setIsLoadingVersionDetails] = useState(false);
  const [isDeletingVersion, setIsDeletingVersion] = useState(false);
  const [isGeneratingVersion, setIsGeneratingVersion] = useState(false);

  const formik = useFormik({
    initialValues: EMPTY_FORM_VALUES,
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const payload = buildSavePayload(
          values,
          editingVersionId,
          expectedRowVersion,
        );
        const response = await onSave(payload);

        toast.success(
          getApiMessage(
            response,
            editingVersionId
              ? "Application version updated successfully."
              : "Application version saved successfully.",
          ),
        );

        resetForm({ values: EMPTY_FORM_VALUES });
        setEditingVersionId(0);
        setExpectedRowVersion(null);
        setFeatureDraft({ moduleId: "", subModuleId: "", feature: "" });
        setFeatureError("");
        setModules([]);
        setSubModules([]);
        setVersionRefreshKey((current) => current + 1);
      } catch (error) {
        toast.error(
          getErrorMessage(error, "Failed to save application version."),
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      const applicationRequest = GetApplicationList()
        .then((response) => {
          const options = unwrapRecords(response)
            .map((item) => ({
              id: String(
                item.Application_ID ??
                item.application_ID ??
                item.ApplicationId ??
                item.applicationId ??
                item.Id ??
                item.id ??
                item.value ??
                "",
              ),
              name:
                item.Application_Name ??
                item.application_Name ??
                item.ApplicationName ??
                item.applicationName ??
                item.Name ??
                item.name ??
                item.label ??
                "",
              teamLeadId:
                item.TeamLeadPersonID ??
                item.teamLeadPersonID ??
                item.TeamLeadPersonId ??
                item.teamLeadPersonId ??
                item.TeamLeadId ??
                item.teamLeadId ??
                "",
              teamLeadName:
                item.TeamLeadName ??
                item.teamLeadName ??
                item.TeamLeadPersonName ??
                item.teamLeadPersonName ??
                item.TeamLead ??
                item.teamLead ??
                "",
              raw: item,
            }))
            .filter((item) => item.id && item.name);

          if (isMounted) setApplications(options);
        })
        .catch(() => {
          if (isMounted) {
            setLoadError("Applications could not be loaded. Please try again.");
            toast.error("Failed to load applications");
          }
        })
        .finally(() => {
          if (isMounted) setIsLoadingApplications(false);
        });

      const employeeRequest = GetApplicationEmployees()
        .then((response) => {
          const options = uniqueOptionsById(
            normalizeLookup(
              unwrapRecords(response),
              [
                "PersonId",
                "PersonID",
                "personId",
                "personID",
                "EmployeeId",
                "employeeId",
                "Id",
                "id",
                "value",
              ],
              [
                "EmployeeName",
                "employeeName",
                "PersonName",
                "personName",
                "Name",
                "name",
                "label",
              ],
            ).map((item) => ({
              ...item,

              designation:
                item.raw?.Designation ??
                item.raw?.designation ??
                "",

              department:
                item.raw?.Department ??
                item.raw?.department ??
                "",
            })),
          );

          if (isMounted) {
            setEmployees(options);
          }

          if (isMounted) setEmployees(options);
        })
        .catch(() => {
          if (isMounted) toast.error("Failed to load employees");
        })
        .finally(() => {
          if (isMounted) setIsLoadingEmployees(false);
        });

      const moduleRequest = GetApplicationsModulesList()
        .then((response) => {
          const options = normalizeModuleOptions(unwrapRecords(response));
          if (isMounted) setAllModules(options);
        })
        .catch(() => {
          if (isMounted) toast.error("Failed to load application modules");
        });

      const subModuleRequest = GetApplicationsSubModulesList()
        .then((response) => {
          const options = normalizeSubModuleOptions(unwrapRecords(response));
          if (isMounted) setAllSubModules(options);
        })
        .catch(() => {
          if (isMounted) toast.error("Failed to load application submodules");
        });

      await Promise.all([
        applicationRequest,
        employeeRequest,
        moduleRequest,
        subModuleRequest,
      ]);
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!loadVersions) return undefined;

    let isMounted = true;

    const fetchVersions = async () => {
      try {
        setIsLoadingVersions(true);
        const response = await loadVersions({
          start: versionPage * versionRowsPerPage,
          length: versionRowsPerPage,
          searchKeyword: appliedVersionSearch,
          sortExpression: "DeploymentDateTime DESC",
          data: {
            applicationId: toIdentifier(formik.values.applicationId),
            status: null,
            environment: null,
          },
        });

        const responseData = response?.data ?? response ?? {};
        const records = unwrapRecords(response);
        const total =
          responseData.totalRecords ??
          responseData.TotalRecords ??
          responseData?.data?.totalRecords ??
          responseData?.data?.TotalRecords ??
          response?.totalRecords ??
          response?.TotalRecords ??
          records[0]?.totalRecords ??
          records[0]?.TotalRecords ??
          records.length;

        if (isMounted) {
          setVersions(records);
          setTotalVersions(Number(total) || 0);
        }
      } catch (error) {
        if (isMounted) toast.error("Failed to load existing versions");
      } finally {
        if (isMounted) setIsLoadingVersions(false);
      }
    };

    fetchVersions();

    return () => {
      isMounted = false;
    };
  }, [
    loadVersions,
    formik.values.applicationId,
    versionPage,
    versionRowsPerPage,
    appliedVersionSearch,
    versionRefreshKey,
  ]);


  useEffect(() => {
    const applicationId = String(
      formik.values.applicationId || "",
    );

    const releaseType = String(
      formik.values.releaseType || "",
    );

    if (
      editingVersionId > 0 ||
      !applicationId ||
      !releaseType
    ) {
      return undefined;
    }

    let isCancelled = false;

    const generateVersionNumber = async () => {
      try {
        setIsGeneratingVersion(true);

        let records = [];

        if (loadVersions) {
          const pageSize = 100;
          let start = 0;
          let totalRecords = Number.MAX_SAFE_INTEGER;
          let pageGuard = 0;

          while (
            start < totalRecords &&
            pageGuard < 100
          ) {
            const response = await loadVersions({
              start,
              length: pageSize,
              searchKeyword: "",
              sortExpression:
                "DeploymentDateTime DESC",
              data: {
                applicationId:
                  toIdentifier(applicationId),
                status: null,
                environment: null,
              },
            });

            const pageRecords =
              unwrapRecords(response);

            records = [
              ...records,
              ...pageRecords,
            ];

            totalRecords =
              getTotalRecordCount(
                response,
                pageRecords,
              );

            if (!pageRecords.length) {
              break;
            }

            start += pageRecords.length;
            pageGuard += 1;

            if (
              pageRecords.length < pageSize &&
              start >= totalRecords
            ) {
              break;
            }
          }
        } else {
          records = (initialVersions || []).filter(
            (item) =>
              String(
                getValue(
                  item,
                  "applicationId",
                  "ApplicationId",
                  "application_ID",
                  "Application_ID",
                ) || "",
              ) === applicationId,
          );
        }

        const nextVersion =
          getNextSemanticVersion(
            records,
            releaseType,
          );

        if (!isCancelled) {
          await formik.setFieldValue(
            "versionNumber",
            nextVersion,
            false,
          );
        }
      } catch (error) {
        if (!isCancelled) {
          await formik.setFieldValue(
            "versionNumber",
            "1.0.0",
            false,
          );

          toast.error(
            getErrorMessage(
              error,
              "Could not calculate the next version number.",
            ),
          );
        }
      } finally {
        if (!isCancelled) {
          setIsGeneratingVersion(false);
        }
      }
    };

    generateVersionNumber();

    return () => {
      isCancelled = true;
    };
  }, [
    editingVersionId,
    formik.values.applicationId,
    formik.values.releaseType,
    initialVersions,
    loadVersions,
    versionRefreshKey,
  ]);

  const fieldError = (fieldName) =>
    formik.touched[fieldName] && Boolean(formik.errors[fieldName]);

  const fieldHelperText = (fieldName) =>
    formik.touched[fieldName] ? formik.errors[fieldName] : "";

  const selectedApplication = useMemo(
    () =>
      applications.find(
        (item) => item.id === String(formik.values.applicationId),
      ),
    [applications, formik.values.applicationId],
  );

  const teamLeadName = useMemo(() => {
    if (!formik.values.teamLeadId) return "";
    return (
      selectedApplication?.teamLeadName ||
      employees.find(
        (employee) => employee.id === String(formik.values.teamLeadId),
      )?.name ||
      "Team Lead ID: " + formik.values.teamLeadId
    );
  }, [employees, formik.values.teamLeadId, selectedApplication]);

  const employeeById = useMemo(
    () =>
      new Map(
        uniqueOptionsById(employees).map((employee) => [
          String(employee.id),
          employee,
        ]),
      ),
    [employees],
  );

  const selectedDeveloperOptions = useMemo(
    () =>
      uniqueStringIds(
        formik.values.developedBy,
      )
        .map((personId) =>
          employeeById.get(String(personId)),
        )
        .filter(Boolean),
    [
      employeeById,
      formik.values.developedBy,
    ],
  );

  const selectedTesterOptions = useMemo(
    () =>
      uniqueStringIds(
        formik.values.testedBy,
      )
        .map((personId) =>
          employeeById.get(String(personId)),
        )
        .filter(Boolean),
    [
      employeeById,
      formik.values.testedBy,
    ],
  );

  const selectedApproverOptions = useMemo(
    () =>
      uniqueStringIds(
        formik.values.approvedBy,
      )
        .map((personId) =>
          employeeById.get(String(personId)),
        )
        .filter(Boolean),
    [
      employeeById,
      formik.values.approvedBy,
    ],
  );

  const getFallbackModules = (application) => {
    const records =
      application?.raw?.modules ||
      application?.raw?.applicationModules ||
      application?.raw?.modulesList ||
      [];

    return normalizeModuleOptions(records);
  };

  const getFallbackSubModules = (module) => {
    const records =
      module?.raw?.subModules ||
      module?.raw?.applicationSubModules ||
      module?.raw?.subModulesList ||
      [];

    return normalizeSubModuleOptions(records);
  };

  const fetchModules = async (applicationId, application) => {
    setModules([]);
    setSubModules([]);

    if (!applicationId) return;

    try {
      setIsLoadingModules(true);

      let options = [];

      if (loadModules) {
        const response = await loadModules(toIdentifier(applicationId));
        options = filterModulesByApplication(
          normalizeModuleOptions(unwrapRecords(response)),
          applicationId,
        );
      } else {
        options = filterModulesByApplication(allModules, applicationId);
      }

      if (!options.length) {
        options = filterModulesByApplication(
          getFallbackModules(application),
          applicationId,
        );
      }

      setModules(options);

      if (!options.length) {
        toast.info("No active modules found for the selected application");
      }
    } catch (error) {
      setModules([]);
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load modules",
      );
    } finally {
      setIsLoadingModules(false);
    }
  };

  const fetchSubModules = async (moduleId) => {
    setSubModules([]);
    if (!moduleId) return;

    const selectedModule = modules.find(
      (item) => item.id === String(moduleId),
    );

    try {
      setIsLoadingSubModules(true);

      let options = [];

      if (loadSubModules) {
        const response = await loadSubModules(toIdentifier(moduleId));
        options = filterSubModulesByModule(
          normalizeSubModuleOptions(unwrapRecords(response)),
          moduleId,
          formik.values.applicationId,
        );
      } else {
        options = filterSubModulesByModule(
          allSubModules,
          moduleId,
          formik.values.applicationId,
        );
      }

      if (!options.length) {
        options = filterSubModulesByModule(
          getFallbackSubModules(selectedModule),
          moduleId,
          formik.values.applicationId,
        );
      }

      setSubModules(options);

      if (!options.length) {
        toast.info("No active submodules found for the selected module");
      }
    } catch (error) {
      setSubModules([]);
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load submodules",
      );
    } finally {
      setIsLoadingSubModules(false);
    }
  };

  useEffect(() => {
    if (loadModules || !formik.values.applicationId) return;

    const filteredModules = filterModulesByApplication(
      allModules,
      formik.values.applicationId,
    );

    setModules(filteredModules);
  }, [allModules, formik.values.applicationId, loadModules]);

  useEffect(() => {
    if (loadSubModules || !featureDraft.moduleId) return;

    const filteredSubModules = filterSubModulesByModule(
      allSubModules,
      featureDraft.moduleId,
      formik.values.applicationId,
    );

    setSubModules(filteredSubModules);
  }, [
    allSubModules,
    featureDraft.moduleId,
    formik.values.applicationId,
    loadSubModules,
  ]);

  const handleApplicationChange = async (event) => {
    const applicationId = String(event.target.value);
    const application = applications.find((item) => item.id === applicationId);

    await formik.setFieldValue("applicationId", applicationId);

    if (editingVersionId === 0) {
      await formik.setFieldValue(
        "versionNumber",
        "",
        false,
      );
    }

    await formik.setFieldValue(
      "teamLeadId",
      application?.teamLeadId ? String(application.teamLeadId) : "",
    );
    await formik.setFieldValue("features", []);
    setFeatureDraft({ moduleId: "", subModuleId: "", feature: "" });
    setFeatureError("");
    setVersionPage(0);
    await fetchModules(applicationId, application);
  };

  const handleReleaseTypeChange = async (event) => {
    const releaseType = String(
      event.target.value || "",
    );

    await formik.setFieldValue(
      "releaseType",
      releaseType,
    );

    if (editingVersionId === 0) {
      await formik.setFieldValue(
        "versionNumber",
        "",
        false,
      );
    }
  };

  const handleModuleChange = async (event) => {
    const moduleId = String(event.target.value);
    setFeatureDraft((current) => ({
      ...current,
      moduleId,
      subModuleId: "",
    }));
    setFeatureError("");
    await fetchSubModules(moduleId);
  };

  const handleAddFeature = async () => {
    const featureText = featureDraft.feature.trim();

    if (!featureDraft.moduleId || !featureText) {
      setFeatureError("Select a module and enter the feature description.");
      return;
    }

    const module = modules.find(
      (item) => item.id === String(featureDraft.moduleId),
    );
    const subModule = subModules.find(
      (item) => item.id === String(featureDraft.subModuleId),
    );

    const newFeature = {
      rowId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      moduleId: featureDraft.moduleId,
      moduleName: module?.name || "",
      subModuleId: featureDraft.subModuleId,
      subModuleName: subModule?.name || "",
      feature: featureText,
    };

    await formik.setFieldValue("features", [
      ...formik.values.features,
      newFeature,
    ]);
    await formik.setFieldTouched("features", true, false);
    setFeatureDraft((current) => ({
      ...current,
      subModuleId: "",
      feature: "",
    }));
    setFeatureError("");
  };

  const handleRemoveFeature = async (rowId) => {
    await formik.setFieldValue(
      "features",
      formik.values.features.filter((item) => item.rowId !== rowId),
    );
  };

  const handleAddDatabaseRow = async (fieldName) => {
    await formik.setFieldValue(fieldName, [
      ...(formik.values[fieldName] || []),
      createDatabaseChangeRow(),
    ]);
  };

  const handleDatabaseRowChange = async (
    fieldName,
    index,
    property,
    value,
  ) => {
    const rows = [...(formik.values[fieldName] || [])];
    const currentRow = rows[index];

    if (!currentRow) return;

    rows[index] = {
      ...currentRow,
      [property]: value,
      ...(property === "schemaOption" && value !== "OTHER"
        ? { customSchemaName: "" }
        : {}),
    };

    await formik.setFieldValue(fieldName, rows);
  };

  const handleRemoveDatabaseRow = async (fieldName, index) => {
    await formik.setFieldValue(
      fieldName,
      (formik.values[fieldName] || []).filter(
        (item, rowIndex) => rowIndex !== index,
      ),
    );
  };


  const handleAddMigrationMapping = async (fieldName) => {
    await formik.setFieldValue(fieldName, [
      ...(formik.values[fieldName] || []),
      createMigrationMappingRow(),
    ]);
  };

  const handleRemoveMigrationMapping = async (fieldName, mappingIndex) => {
    await formik.setFieldValue(
      fieldName,
      (formik.values[fieldName] || []).filter(
        (item, index) => index !== mappingIndex,
      ),
    );
  };

  const handleAddMigrationObject = async (
    fieldName,
    mappingIndex,
    side,
  ) => {
    const mappings = [...(formik.values[fieldName] || [])];
    const mapping = mappings[mappingIndex];
    if (!mapping) return;

    mappings[mappingIndex] = {
      ...mapping,
      [side]: [...(mapping[side] || []), createMigrationSideObject()],
    };

    await formik.setFieldValue(fieldName, mappings);
  };

  const handleRemoveMigrationObject = async (
    fieldName,
    mappingIndex,
    side,
    objectIndex,
  ) => {
    const mappings = [...(formik.values[fieldName] || [])];
    const mapping = mappings[mappingIndex];
    if (!mapping || (mapping[side] || []).length <= 1) return;

    mappings[mappingIndex] = {
      ...mapping,
      [side]: (mapping[side] || []).filter(
        (item, index) => index !== objectIndex,
      ),
    };

    await formik.setFieldValue(fieldName, mappings);
  };

  const handleMigrationObjectChange = async (
    fieldName,
    mappingIndex,
    side,
    objectIndex,
    property,
    value,
  ) => {
    const mappings = [...(formik.values[fieldName] || [])];
    const mapping = mappings[mappingIndex];
    const objects = [...(mapping?.[side] || [])];
    const currentObject = objects[objectIndex];

    if (!mapping || !currentObject) return;

    objects[objectIndex] = {
      ...currentObject,
      [property]: value,
      ...(property === "schemaOption" && value !== "OTHER"
        ? { customSchemaName: "" }
        : {}),
    };

    mappings[mappingIndex] = {
      ...mapping,
      [side]: objects,
    };

    await formik.setFieldValue(fieldName, mappings);
  };

  const toggleArrayValue = async (fieldName, value) => {
    const currentValues = formik.values[fieldName] || [];
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];
    await formik.setFieldValue(fieldName, nextValues);
  };

  const handleReset = () => {
    formik.resetForm({ values: EMPTY_FORM_VALUES });
    setEditingVersionId(0);
    setExpectedRowVersion(null);
    setModules([]);
    setSubModules([]);
    setFeatureDraft({ moduleId: "", subModuleId: "", feature: "" });
    setFeatureError("");
  };

  const loadCompleteVersion = async (applicationVersionId) => {
    if (!applicationVersionId) {
      throw new Error("Application version ID is missing.");
    }
    return loadVersionById({ applicationVersionId: Number(applicationVersionId) });
  };

  const handleViewVersion = async (version) => {
    try {
      setIsLoadingVersionDetails(true);
      const response = await loadCompleteVersion(version.id);
      setViewDetails(getApiData(response));
      setIsViewOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load version details."));
    } finally {
      setIsLoadingVersionDetails(false);
    }
  };

  const handleEditVersion = async (version) => {
    try {
      setIsLoadingVersionDetails(true);
      const response = await loadCompleteVersion(version.id);
      const mapped = buildEditFormValues(response);

      formik.resetForm({ values: mapped.values });
      setEditingVersionId(mapped.applicationVersionId);
      setExpectedRowVersion(mapped.expectedRowVersion);
      setFeatureDraft({ moduleId: "", subModuleId: "", feature: "" });
      setFeatureError("");

      const application = applications.find(
        (item) => item.id === String(mapped.values.applicationId),
      );
      await fetchModules(mapped.values.applicationId, application);

      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.info("Version loaded in edit mode.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load version for editing."));
    } finally {
      setIsLoadingVersionDetails(false);
    }
  };

  const handleDeleteVersion = async () => {
    if (!deleteTarget?.id) return;

    try {
      setIsDeletingVersion(true);
      const response = await deleteVersion({
        applicationVersionId: Number(deleteTarget.id),
      });
      toast.success(
        getApiMessage(response, "Application version deleted successfully."),
      );
      setDeleteTarget(null);
      if (Number(editingVersionId) === Number(deleteTarget.id)) {
        handleReset();
      }
      setVersionRefreshKey((current) => current + 1);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete application version."));
    } finally {
      setIsDeletingVersion(false);
    }
  };

  const normalizeVersion = (item) => ({
    id: item.id ?? item.applicationVersionId ?? item.versionId,
    applicationId:
      item.applicationId ?? item.application_ID ?? item.applicationIdFk ?? "",
    applicationName:
      item.applicationName ?? item.application_Name ?? item.application ?? "-",
    versionNumber: item.versionNumber ?? item.applicationVersion ?? "-",
    releaseName: item.releaseName ?? "-",
    releaseType: item.releaseType ?? "-",
    status: item.status ?? "-",
    buildNumber: item.buildNumber ?? "-",
    environment: item.environment ?? "-",
    deploymentDate: item.deploymentDate ?? item.deploymentDateTime ?? null,
    teamLeadName: item.teamLeadName ?? item.teamLead ?? "-",
    rowVersion: item.rowVersion ?? item.RowVersion ?? null,
  });

  const localFilteredVersions = useMemo(() => {
    if (loadVersions) return [];

    const keyword = appliedVersionSearch.toLowerCase();
    return initialVersions.map(normalizeVersion).filter((item) => {
      const matchesApplication = formik.values.applicationId
        ? String(item.applicationId) === String(formik.values.applicationId)
        : true;
      const matchesSearch = keyword
        ? Object.values(item).some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(keyword),
        )
        : true;
      return matchesApplication && matchesSearch;
    });
  }, [
    appliedVersionSearch,
    formik.values.applicationId,
    initialVersions,
    loadVersions,
  ]);

  const versionRows = loadVersions
    ? versions.map(normalizeVersion)
    : localFilteredVersions.slice(
      versionPage * versionRowsPerPage,
      versionPage * versionRowsPerPage + versionRowsPerPage,
    );

  const versionCount = loadVersions
    ? totalVersions
    : localFilteredVersions.length;

  const getStatusChipSx = (status) => {
    const normalizedStatus = String(status).toLowerCase();
    if (normalizedStatus === "release" || normalizedStatus === "released") {
      return { bgcolor: "success.light", color: "success.dark" };
    }
    if (normalizedStatus === "testing") {
      return { bgcolor: "warning.light", color: "warning.dark" };
    }
    if (normalizedStatus === "development") {
      return { bgcolor: "primary.light", color: "primary.main" };
    }
    return { bgcolor: "action.hover", color: "text.secondary" };
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const rawValue = String(value).trim();

    const dayFirstMatch = rawValue.match(
      /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2})(?::\d{2})?\s*(AM|PM)?)?/i,
    );

    if (dayFirstMatch) {
      const [, day, month, year, hour, minute, meridiem] =
        dayFirstMatch;

      return hour && minute
        ? `${day}-${month}-${year} ${hour}:${minute}${meridiem ? ` ${meridiem.toUpperCase()}` : ""}`
        : `${day}-${month}-${year}`;
    }

    const yearFirstMatch = rawValue.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/,
    );

    if (yearFirstMatch) {
      const [, year, month, day, hour, minute] =
        yearFirstMatch;

      if (!hour || !minute) {
        return `${day}-${month}-${year}`;
      }
    }

    const date = new Date(rawValue);

    if (Number.isNaN(date.getTime())) {
      return rawValue;
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box sx={compactFormSx}>
      <Box component="form" onSubmit={formik.handleSubmit} noValidate>

        {editingVersionId > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleReset}>
                Cancel Edit
              </Button>
            }
          >
            Editing application version ID <strong>{editingVersionId}</strong>.
            RowVersion concurrency protection is enabled.
          </Alert>
        )}

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        <SectionCard
          icon="info"
          title="Release Information"
          subtitle="Application version, build, environment, and deployment details"
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="applicationId">
                Application <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="applicationId"
                name="applicationId"
                select
                fullWidth
                value={formik.values.applicationId}
                onChange={handleApplicationChange}
                onBlur={formik.handleBlur}
                error={fieldError("applicationId")}
                helperText={fieldHelperText("applicationId")}
                disabled={isLoadingApplications}
                SelectProps={compactSelectProps}
              >
                <MenuItem value="" disabled>
                  {isLoadingApplications
                    ? "Loading applications..."
                    : "Select application"}
                </MenuItem>
                {applications.map((application) => (
                  <MenuItem key={application.id} value={application.id}>
                    {application.name}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="versionNumber">
                Version Number <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="versionNumber"
                name="versionNumber"
                fullWidth
                placeholder={
                  formik.values.applicationId &&
                    formik.values.releaseType
                    ? "Calculating next version..."
                    : "Select application and release type"
                }
                value={formik.values.versionNumber}
                onBlur={formik.handleBlur}
                error={fieldError("versionNumber")}
                helperText={
                  fieldHelperText("versionNumber") ||
                  (editingVersionId > 0
                    ? "The saved version number is retained during edit."
                    : "Generated automatically from the latest version and release type.")
                }
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      {isGeneratingVersion ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Tooltip title="Version number is generated automatically">
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              color: "text.secondary",
                            }}
                          >
                            <FeatherIcon
                              icon="refresh-cw"
                              size="15"
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="releaseName">
                Release Name <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="releaseName"
                name="releaseName"
                fullWidth
                placeholder="Example: July Production Release"
                value={formik.values.releaseName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("releaseName")}
                helperText={fieldHelperText("releaseName")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="releaseType">
                Release Type <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="releaseType"
                name="releaseType"
                select
                fullWidth
                value={formik.values.releaseType}
                onChange={handleReleaseTypeChange}
                onBlur={formik.handleBlur}
                error={fieldError("releaseType")}
                helperText={fieldHelperText("releaseType")}
                SelectProps={compactSelectProps}
              >
                <MenuItem value="" disabled>
                  Select release type
                </MenuItem>
                {releaseTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="status">
                Status <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="status"
                name="status"
                select
                fullWidth
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("status")}
                helperText={fieldHelperText("status")}
                SelectProps={compactSelectProps}
              >
                {releaseStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="buildNumber">
                Build Number <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="buildNumber"
                name="buildNumber"
                fullWidth
                placeholder="Example: BUILD-2026.07.22.01"
                value={formik.values.buildNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("buildNumber")}
                helperText={fieldHelperText("buildNumber")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="environment">
                Environment <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="environment"
                name="environment"
                select
                fullWidth
                value={formik.values.environment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("environment")}
                helperText={fieldHelperText("environment")}
                SelectProps={compactSelectProps}
              >
                {environments.map((environment) => (
                  <MenuItem key={environment} value={environment}>
                    {environment}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="deploymentDate">
                Deployment Date <RequiredMark />
              </CustomFormLabel>

              <CustomTextField
                id="deploymentDate"
                name="deploymentDate"
                type="date"
                fullWidth
                value={formik.values.deploymentDate || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("deploymentDate")}
                helperText={
                  fieldHelperText("deploymentDate") ||
                  "Deployment time will be added automatically."
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: "9999-12-31",
                }}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          icon="users"
          title="Development Details"
          subtitle="People, ownership, source branch, and version summary"
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <CustomFormLabel>
                Developed By <RequiredMark />
              </CustomFormLabel>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={employees}
                value={selectedDeveloperOptions}
                loading={isLoadingEmployees}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={async (event, selected) => {
                  await formik.setFieldValue(
                    "developedBy",
                    selected.map((item) => item.id),
                  );
                  await formik.setFieldTouched("developedBy", true, false);
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      {(option.designation || option.department) && (
                        <Typography variant="caption" color="text.secondary">
                          {[option.designation, option.department]
                            .filter(Boolean)
                            .join(" • ")}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder="Select developers"
                    error={fieldError("developedBy")}
                    helperText={fieldHelperText("developedBy")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel>
                Tested By <RequiredMark />
              </CustomFormLabel>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={employees}
                value={selectedTesterOptions}
                loading={isLoadingEmployees}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={async (event, selected) => {
                  await formik.setFieldValue(
                    "testedBy",
                    selected.map((item) => item.id),
                  );
                  await formik.setFieldTouched("testedBy", true, false);
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      {(option.designation || option.department) && (
                        <Typography variant="caption" color="text.secondary">
                          {[option.designation, option.department]
                            .filter(Boolean)
                            .join(" • ")}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder="Select testers"
                    error={fieldError("testedBy")}
                    helperText={fieldHelperText("testedBy")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel>
                Approved By <RequiredMark />
              </CustomFormLabel>

              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                filterSelectedOptions
                options={employees}
                value={selectedApproverOptions}
                loading={isLoadingEmployees}
                disabled={isLoadingEmployees}
                getOptionLabel={(option) =>
                  option?.name || ""
                }
                isOptionEqualToValue={(option, value) =>
                  String(option?.id) ===
                  String(value?.id)
                }
                onChange={async (event, selected) => {
                  await formik.setFieldValue(
                    "approvedBy",
                    uniqueStringIds(
                      selected.map(
                        (item) => item.id,
                      ),
                    ),
                  );

                  await formik.setFieldTouched(
                    "approvedBy",
                    true,
                    false,
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched(
                    "approvedBy",
                    true,
                    false,
                  );
                }}
                renderOption={(
                  props,
                  option,
                  { selected },
                ) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 1 }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">
                        {option.name}
                      </Typography>

                      {(option.designation ||
                        option.department) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {[
                              option.designation,
                              option.department,
                            ]
                              .filter(Boolean)
                              .join(" • ")}
                          </Typography>
                        )}
                    </Box>
                  </li>
                )}
                renderTags={(
                  selected,
                  getTagProps,
                ) =>
                  selected.map(
                    (option, index) => (
                      <Chip
                        {...getTagProps({
                          index,
                        })}
                        key={option.id}
                        size="small"
                        label={option.name}
                      />
                    ),
                  )
                }
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder={
                      selectedApproverOptions.length
                        ? ""
                        : "Select approvers"
                    }
                    error={fieldError(
                      "approvedBy",
                    )}
                    helperText={fieldHelperText(
                      "approvedBy",
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="teamLeadName">
                Team Lead <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="teamLeadName"
                fullWidth
                value={teamLeadName}
                placeholder="Select an application to load Team Lead"
                error={fieldError("teamLeadId")}
                helperText={fieldHelperText("teamLeadId")}
                InputProps={{ readOnly: true }}
                sx={{ bgcolor: "action.hover" }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="gitBranch">
                Git Branch <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="gitBranch"
                name="gitBranch"
                fullWidth
                placeholder="Example: release/v2.1.0"
                value={formik.values.gitBranch}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("gitBranch")}
                helperText={fieldHelperText("gitBranch")}
              />
            </Grid>

            <Grid item xs={12} md={9}>
              <CustomFormLabel htmlFor="versionDescription">
                Version Description <RequiredMark />
              </CustomFormLabel>
              <CustomTextField
                id="versionDescription"
                name="versionDescription"
                fullWidth
                multiline
                minRows={4}
                placeholder="Describe the purpose, scope, major changes, and impact of this version..."
                value={formik.values.versionDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("versionDescription")}
                helperText={fieldHelperText("versionDescription")}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          icon="gift"
          title="What's New"
          subtitle="Add one or more module-wise features included in this version"
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="featureModule">Module</CustomFormLabel>
              <CustomTextField
                id="featureModule"
                select
                fullWidth
                value={featureDraft.moduleId}
                onChange={handleModuleChange}
                disabled={!formik.values.applicationId || isLoadingModules}
                SelectProps={compactSelectProps}
              >
                <MenuItem value="" disabled>
                  {isLoadingModules
                    ? "Loading modules..."
                    : modules.length
                      ? "Select module"
                      : "No modules found"}
                </MenuItem>
                {modules.map((module) => (
                  <MenuItem key={module.id} value={module.id}>
                    {module.name}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomFormLabel htmlFor="featureSubModule">
                Sub Module
              </CustomFormLabel>
              <CustomTextField
                id="featureSubModule"
                select
                fullWidth
                value={featureDraft.subModuleId}
                onChange={(event) =>
                  setFeatureDraft((current) => ({
                    ...current,
                    subModuleId: String(event.target.value),
                  }))
                }
                disabled={!featureDraft.moduleId || isLoadingSubModules}
                SelectProps={compactSelectProps}
              >
                <MenuItem value="">
                  {isLoadingSubModules
                    ? "Loading submodules..."
                    : subModules.length
                      ? "Select submodule"
                      : "No submodule found"}
                </MenuItem>
                {subModules.map((subModule) => (
                  <MenuItem key={subModule.id} value={subModule.id}>
                    {subModule.name}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <CustomFormLabel htmlFor="featureText">Feature</CustomFormLabel>
              <CustomTextField
                id="featureText"
                fullWidth
                placeholder="Enter feature or change details"
                value={featureDraft.feature}
                onChange={(event) => {
                  setFeatureDraft((current) => ({
                    ...current,
                    feature: event.target.value,
                  }));
                  setFeatureError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleAddFeature();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <CustomFormLabel>&nbsp;</CustomFormLabel>
              <Button
                type="button"
                fullWidth
                variant="contained"
                startIcon={<FeatherIcon icon="plus" size="17" />}
                onClick={handleAddFeature}
                sx={{ minHeight: 40 }}
              >
                Add
              </Button>
            </Grid>
          </Grid>

          {(featureError || fieldHelperText("features")) && (
            <Typography color="error" variant="caption" display="block" mt={1}>
              {featureError || fieldHelperText("features")}
            </Typography>
          )}

          <Typography variant="subtitle1" fontWeight={700} mt={3} mb={1.5}>
            List of Features ({formik.values.features.length})
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: "action.hover" }}>
                <TableRow>
                  <TableCell width="25%" sx={{ fontWeight: 700 }}>
                    Module
                  </TableCell>
                  <TableCell width="25%" sx={{ fontWeight: 700 }}>
                    Sub Module
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                  <TableCell align="center" width={80} sx={{ fontWeight: 700 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formik.values.features.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No features added yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  formik.values.features.map((feature, index) => (
                    <TableRow key={feature.rowId} hover>
                      <TableCell>{feature.moduleName}</TableCell>
                      <TableCell>{feature.subModuleName || "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                        {feature.feature}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove feature">
                          <IconButton
                            type="button"
                            size="small"
                            color="error"
                            onClick={() => handleRemoveFeature(feature.rowId)}
                            aria-label={`Remove feature ${index + 1}`}
                          >
                            <FeatherIcon icon="trash-2" size="17" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>

        <SectionCard
          icon="database"
          title="Database Changes"
          subtitle="Add schema-wise details for every new or modified table and stored procedure"
        >
          <DatabaseObjectSection
            title="Tables Added"
            subtitle="New tables created in this version and their business or technical purpose"
            addButtonLabel="Add Table"
            objectNameLabel="New Table Name"
            descriptionLabel="Purpose / Use of Table"
            rows={formik.values.databaseTablesAdded}
            fieldName="databaseTablesAdded"
            errors={formik.errors.databaseTablesAdded}
            touched={formik.touched.databaseTablesAdded}
            showIndexes
            onAdd={() => handleAddDatabaseRow("databaseTablesAdded")}
            onChange={(index, property, value) =>
              handleDatabaseRowChange(
                "databaseTablesAdded",
                index,
                property,
                value,
              )
            }
            onRemove={(index) =>
              handleRemoveDatabaseRow("databaseTablesAdded", index)
            }
            onBlur={formik.handleBlur}
          />

          <DatabaseObjectSection
            title="Tables Modified"
            subtitle="Existing tables changed in this version and the exact modification performed"
            addButtonLabel="Add Modified Table"
            objectNameLabel="Existing Table Name"
            descriptionLabel="Changes Made in Table"
            rows={formik.values.databaseTablesModified}
            fieldName="databaseTablesModified"
            errors={formik.errors.databaseTablesModified}
            touched={formik.touched.databaseTablesModified}
            showIndexes
            onAdd={() => handleAddDatabaseRow("databaseTablesModified")}
            onChange={(index, property, value) =>
              handleDatabaseRowChange(
                "databaseTablesModified",
                index,
                property,
                value,
              )
            }
            onRemove={(index) =>
              handleRemoveDatabaseRow("databaseTablesModified", index)
            }
            onBlur={formik.handleBlur}
          />

          <DatabaseObjectSection
            title="Stored Procedures Added"
            subtitle="New stored procedures created in this version and what each procedure is used for"
            addButtonLabel="Add SP"
            objectNameLabel="New Stored Procedure Name"
            descriptionLabel="Purpose / Use of Stored Procedure"
            rows={formik.values.databaseStoredProceduresAdded}
            fieldName="databaseStoredProceduresAdded"
            errors={formik.errors.databaseStoredProceduresAdded}
            touched={formik.touched.databaseStoredProceduresAdded}
            onAdd={() =>
              handleAddDatabaseRow("databaseStoredProceduresAdded")
            }
            onChange={(index, property, value) =>
              handleDatabaseRowChange(
                "databaseStoredProceduresAdded",
                index,
                property,
                value,
              )
            }
            onRemove={(index) =>
              handleRemoveDatabaseRow(
                "databaseStoredProceduresAdded",
                index,
              )
            }
            onBlur={formik.handleBlur}
          />

          <DatabaseObjectSection
            title="Stored Procedures Modified"
            subtitle="Existing stored procedures changed in this version and the exact modification performed"
            addButtonLabel="Add Modified SP"
            objectNameLabel="Existing Stored Procedure Name"
            descriptionLabel="Changes Made in Stored Procedure"
            rows={formik.values.databaseStoredProceduresModified}
            fieldName="databaseStoredProceduresModified"
            errors={formik.errors.databaseStoredProceduresModified}
            touched={formik.touched.databaseStoredProceduresModified}
            onAdd={() =>
              handleAddDatabaseRow("databaseStoredProceduresModified")
            }
            onChange={(index, property, value) =>
              handleDatabaseRowChange(
                "databaseStoredProceduresModified",
                index,
                property,
                value,
              )
            }
            onRemove={(index) =>
              handleRemoveDatabaseRow(
                "databaseStoredProceduresModified",
                index,
              )
            }
            onBlur={formik.handleBlur}
          />

          <Card
            sx={{
              p: 0,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: "action.hover",
                borderBottom: formik.values.dataMigration ? "1px solid" : 0,
                borderColor: "divider",
              }}
            >
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={formik.values.dataMigration}
                    onChange={async (event) => {
                      const checked = event.target.checked;
                      await formik.setFieldValue("dataMigration", checked);

                      if (!checked) {
                        await Promise.all([
                          formik.setFieldValue(
                            "dataMigrationSourceDatabaseOption",
                            "",
                          ),
                          formik.setFieldValue(
                            "dataMigrationSourceCustomDatabaseName",
                            "",
                          ),
                          formik.setFieldValue(
                            "dataMigrationTargetDatabaseOption",
                            "",
                          ),
                          formik.setFieldValue(
                            "dataMigrationTargetCustomDatabaseName",
                            "",
                          ),
                          formik.setFieldValue("dataMigrationTables", []),
                          formik.setFieldValue(
                            "dataMigrationStoredProcedures",
                            [],
                          ),
                          formik.setFieldValue(
                            "dataMigrationDescription",
                            "",
                          ),
                        ]);
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight={700} color="primary.main">
                      Data Migration
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Select source and target databases, then map every old
                      table or stored procedure to its new database object.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {formik.values.dataMigration && (
              <CardContent
                sx={{
                  padding: { xs: "16px", sm: "14px 30px" },
                  "&:last-child": { pb: { xs: 2, sm: "14px" } },
                }}
              >
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid
                    item
                    xs={12}
                    md={
                      formik.values.dataMigrationSourceDatabaseOption ===
                        "OTHER"
                        ? 3
                        : 6
                    }
                  >
                    <CustomFormLabel htmlFor="dataMigrationSourceDatabaseOption">
                      From Database <RequiredMark />
                    </CustomFormLabel>
                    <CustomTextField
                      id="dataMigrationSourceDatabaseOption"
                      name="dataMigrationSourceDatabaseOption"
                      select
                      fullWidth
                      value={formik.values.dataMigrationSourceDatabaseOption}
                      onChange={async (event) => {
                        await formik.setFieldValue(
                          "dataMigrationSourceDatabaseOption",
                          event.target.value,
                        );
                        if (event.target.value !== "OTHER") {
                          await formik.setFieldValue(
                            "dataMigrationSourceCustomDatabaseName",
                            "",
                          );
                        }
                        await formik.setFieldValue("dataMigrationTables", []);
                        await formik.setFieldValue(
                          "dataMigrationStoredProcedures",
                          [],
                        );
                      }}
                      onBlur={formik.handleBlur}
                      error={fieldError("dataMigrationSourceDatabaseOption")}
                      helperText={fieldHelperText(
                        "dataMigrationSourceDatabaseOption",
                      )}
                      SelectProps={compactSelectProps}
                    >
                      <MenuItem value="" disabled>
                        Select source database
                      </MenuItem>
                      {DATABASE_OPTIONS.map((database) => (
                        <MenuItem key={database.value} value={database.value}>
                          {database.label}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>

                  {formik.values.dataMigrationSourceDatabaseOption ===
                    "OTHER" && (
                      <Grid item xs={12} md={3}>
                        <CustomFormLabel htmlFor="dataMigrationSourceCustomDatabaseName">
                          Custom From Database <RequiredMark />
                        </CustomFormLabel>
                        <CustomTextField
                          id="dataMigrationSourceCustomDatabaseName"
                          name="dataMigrationSourceCustomDatabaseName"
                          fullWidth
                          placeholder="Enter source database name"
                          value={
                            formik.values.dataMigrationSourceCustomDatabaseName
                          }
                          onChange={async (event) => {
                            await formik.setFieldValue(
                              "dataMigrationSourceCustomDatabaseName",
                              event.target.value,
                            );
                            await formik.setFieldValue(
                              "dataMigrationTables",
                              [],
                            );
                            await formik.setFieldValue(
                              "dataMigrationStoredProcedures",
                              [],
                            );
                          }}
                          onBlur={formik.handleBlur}
                          error={fieldError(
                            "dataMigrationSourceCustomDatabaseName",
                          )}
                          helperText={fieldHelperText(
                            "dataMigrationSourceCustomDatabaseName",
                          )}
                          inputProps={{ maxLength: 128 }}
                        />
                      </Grid>
                    )}

                  <Grid
                    item
                    xs={12}
                    md={
                      formik.values.dataMigrationTargetDatabaseOption ===
                        "OTHER"
                        ? 3
                        : 6
                    }
                  >
                    <CustomFormLabel htmlFor="dataMigrationTargetDatabaseOption">
                      To Database <RequiredMark />
                    </CustomFormLabel>
                    <CustomTextField
                      id="dataMigrationTargetDatabaseOption"
                      name="dataMigrationTargetDatabaseOption"
                      select
                      fullWidth
                      value={formik.values.dataMigrationTargetDatabaseOption}
                      onChange={async (event) => {
                        await formik.setFieldValue(
                          "dataMigrationTargetDatabaseOption",
                          event.target.value,
                        );
                        if (event.target.value !== "OTHER") {
                          await formik.setFieldValue(
                            "dataMigrationTargetCustomDatabaseName",
                            "",
                          );
                        }
                        await formik.setFieldValue("dataMigrationTables", []);
                        await formik.setFieldValue(
                          "dataMigrationStoredProcedures",
                          [],
                        );
                      }}
                      onBlur={formik.handleBlur}
                      error={fieldError("dataMigrationTargetDatabaseOption")}
                      helperText={fieldHelperText(
                        "dataMigrationTargetDatabaseOption",
                      )}
                      SelectProps={compactSelectProps}
                    >
                      <MenuItem value="" disabled>
                        Select target database
                      </MenuItem>
                      {DATABASE_OPTIONS.map((database) => (
                        <MenuItem key={database.value} value={database.value}>
                          {database.label}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>

                  {formik.values.dataMigrationTargetDatabaseOption ===
                    "OTHER" && (
                      <Grid item xs={12} md={3}>
                        <CustomFormLabel htmlFor="dataMigrationTargetCustomDatabaseName">
                          Custom To Database <RequiredMark />
                        </CustomFormLabel>
                        <CustomTextField
                          id="dataMigrationTargetCustomDatabaseName"
                          name="dataMigrationTargetCustomDatabaseName"
                          fullWidth
                          placeholder="Enter target database name"
                          value={
                            formik.values.dataMigrationTargetCustomDatabaseName
                          }
                          onChange={async (event) => {
                            await formik.setFieldValue(
                              "dataMigrationTargetCustomDatabaseName",
                              event.target.value,
                            );
                            await formik.setFieldValue(
                              "dataMigrationTables",
                              [],
                            );
                            await formik.setFieldValue(
                              "dataMigrationStoredProcedures",
                              [],
                            );
                          }}
                          onBlur={formik.handleBlur}
                          error={fieldError(
                            "dataMigrationTargetCustomDatabaseName",
                          )}
                          helperText={fieldHelperText(
                            "dataMigrationTargetCustomDatabaseName",
                          )}
                          inputProps={{ maxLength: 128 }}
                        />
                      </Grid>
                    )}
                </Grid>

                <MigrationMappingSection
                  title="Table Migration Mapping"
                  subtitle="Create flexible old-to-new table mappings: one-to-one, one-to-many split, many-to-one merge, or many-to-many"
                  addButtonLabel="Add Table Mapping"
                  objectLabel="Table"
                  rows={formik.values.dataMigrationTables}
                  fieldName="dataMigrationTables"
                  errors={formik.errors.dataMigrationTables}
                  touched={formik.touched.dataMigrationTables}
                  showIndexes
                  sourceDatabaseName={resolveMigrationDatabaseName(
                    formik.values.dataMigrationSourceDatabaseOption,
                    formik.values.dataMigrationSourceCustomDatabaseName,
                  )}
                  targetDatabaseName={resolveMigrationDatabaseName(
                    formik.values.dataMigrationTargetDatabaseOption,
                    formik.values.dataMigrationTargetCustomDatabaseName,
                  )}
                  onAddMapping={() =>
                    handleAddMigrationMapping("dataMigrationTables")
                  }
                  onRemoveMapping={(mappingIndex) =>
                    handleRemoveMigrationMapping(
                      "dataMigrationTables",
                      mappingIndex,
                    )
                  }
                  onAddObject={(mappingIndex, side) =>
                    handleAddMigrationObject(
                      "dataMigrationTables",
                      mappingIndex,
                      side,
                    )
                  }
                  onRemoveObject={(mappingIndex, side, objectIndex) =>
                    handleRemoveMigrationObject(
                      "dataMigrationTables",
                      mappingIndex,
                      side,
                      objectIndex,
                    )
                  }
                  onObjectChange={(
                    mappingIndex,
                    side,
                    objectIndex,
                    property,
                    value,
                  ) =>
                    handleMigrationObjectChange(
                      "dataMigrationTables",
                      mappingIndex,
                      side,
                      objectIndex,
                      property,
                      value,
                    )
                  }
                  onBlur={formik.handleBlur}
                />

                <MigrationMappingSection
                  title="Stored Procedure Migration Mapping"
                  subtitle="Create flexible stored procedure split/merge mappings with optional function and type names"
                  addButtonLabel="Add SP Mapping"
                  objectLabel="Stored Procedure"
                  rows={formik.values.dataMigrationStoredProcedures}
                  fieldName="dataMigrationStoredProcedures"
                  errors={formik.errors.dataMigrationStoredProcedures}
                  touched={formik.touched.dataMigrationStoredProcedures}
                  sourceDatabaseName={resolveMigrationDatabaseName(
                    formik.values.dataMigrationSourceDatabaseOption,
                    formik.values.dataMigrationSourceCustomDatabaseName,
                  )}
                  targetDatabaseName={resolveMigrationDatabaseName(
                    formik.values.dataMigrationTargetDatabaseOption,
                    formik.values.dataMigrationTargetCustomDatabaseName,
                  )}
                  onAddMapping={() =>
                    handleAddMigrationMapping(
                      "dataMigrationStoredProcedures",
                    )
                  }
                  onRemoveMapping={(mappingIndex) =>
                    handleRemoveMigrationMapping(
                      "dataMigrationStoredProcedures",
                      mappingIndex,
                    )
                  }
                  onAddObject={(mappingIndex, side) =>
                    handleAddMigrationObject(
                      "dataMigrationStoredProcedures",
                      mappingIndex,
                      side,
                    )
                  }
                  onRemoveObject={(mappingIndex, side, objectIndex) =>
                    handleRemoveMigrationObject(
                      "dataMigrationStoredProcedures",
                      mappingIndex,
                      side,
                      objectIndex,
                    )
                  }
                  onObjectChange={(
                    mappingIndex,
                    side,
                    objectIndex,
                    property,
                    value,
                  ) =>
                    handleMigrationObjectChange(
                      "dataMigrationStoredProcedures",
                      mappingIndex,
                      side,
                      objectIndex,
                      property,
                      value,
                    )
                  }
                  onBlur={formik.handleBlur}
                />

                {formik.touched.dataMigrationTables &&
                  typeof formik.errors.dataMigrationTables === "string" && (
                    <Typography
                      color="error"
                      variant="caption"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {formik.errors.dataMigrationTables}
                    </Typography>
                  )}

                <Box sx={{ mt: 2 }}>
                  <CustomFormLabel htmlFor="dataMigrationDescription">
                    Migration Details <RequiredMark />
                  </CustomFormLabel>
                  <CustomTextField
                    id="dataMigrationDescription"
                    name="dataMigrationDescription"
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Describe migration reason, scripts, execution order, validation steps, dependencies, and rollback plan..."
                    value={formik.values.dataMigrationDescription}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={fieldError("dataMigrationDescription")}
                    helperText={fieldHelperText("dataMigrationDescription")}
                    inputProps={{ maxLength: 2000 }}
                  />
                </Box>
              </CardContent>
            )}
          </Card>
        </SectionCard>

        <SectionCard
          icon="check-square"
          title="Deployment Checklist"
          subtitle="Select completed release-readiness tasks from the static application checklist"
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomFormLabel htmlFor="deploymentChecklist">
                Completed Checklist Items
              </CustomFormLabel>
              <CustomTextField
                id="deploymentChecklist"
                name="deploymentChecklist"
                select
                fullWidth
                value={formik.values.deploymentChecklist}
                onChange={async (event) => {
                  const selectedValue = event.target.value;
                  await formik.setFieldValue(
                    "deploymentChecklist",
                    typeof selectedValue === "string"
                      ? selectedValue.split(",")
                      : selectedValue,
                  );
                }}
                onBlur={formik.handleBlur}
                error={fieldError("deploymentChecklist")}
                helperText={
                  fieldHelperText("deploymentChecklist") ||
                  "Static checklist keys are stored with the version record."
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "auto !important",
                    minHeight: 40,
                    alignItems: "center",
                  },
                  "& .MuiSelect-select": {
                    minHeight: "24px !important",
                    flexWrap: "wrap",
                  },
                }}
                SelectProps={{
                  ...compactSelectProps,
                  multiple: true,
                  renderValue: (selectedIds) => {
                    if (!selectedIds?.length) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          Select completed checklist items
                        </Typography>
                      );
                    }

                    const selectedOptions = deploymentChecklistOptions.filter(
                      (option) => selectedIds.includes(option.id),
                    );

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          maxWidth: "100%",
                        }}
                      >
                        {selectedOptions.map((option) => (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            sx={{ maxWidth: 240 }}
                          />
                        ))}
                      </Box>
                    );
                  },
                }}
              >
                {deploymentChecklistOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    <Checkbox
                      size="small"
                      checked={formik.values.deploymentChecklist.includes(
                        option.id,
                      )}
                      sx={{ mr: 1 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option.name}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>
          </Grid>

          <CustomFormLabel htmlFor="remarks">Remarks</CustomFormLabel>
          <CustomTextField
            id="remarks"
            name="remarks"
            fullWidth
            multiline
            minRows={4}
            placeholder="Add deployment instructions, known issues, dependencies, or other remarks..."
            value={formik.values.remarks}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={fieldError("remarks")}
            helperText={fieldHelperText("remarks")}
          />
        </SectionCard>

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          justifyContent="flex-end"
          spacing={1.5}
          sx={{ mb: 4 }}
        >
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            startIcon={<FeatherIcon icon="rotate-ccw" size="17" />}
            onClick={handleReset}
            disabled={formik.isSubmitting}
            sx={{ minWidth: 120 }}
          >
            Reset
          </Button>

          <Button
            type="submit"
            variant="contained"
            startIcon={
              formik.isSubmitting ? (
                <CircularProgress color="inherit" size={17} />
              ) : (
                <FeatherIcon icon="save" size="17" />
              )
            }
            disabled={
              formik.isSubmitting || isLoadingApplications || isLoadingEmployees
            }
            sx={{ minWidth: 160 }}
          >
            {formik.isSubmitting
              ? "Saving..."
              : editingVersionId > 0
                ? "Update Version"
                : "Save Version"}
          </Button>
        </Stack>
      </Box>

      <Card
        sx={{
          p: 0,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            bgcolor: "primary.light",
            color: "primary.main",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                Existing Versions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review application release history and deployment status.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <CustomTextField
                size="small"
                placeholder="Search versions..."
                value={versionSearchText}
                onChange={(event) => setVersionSearchText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    setVersionPage(0);
                    setAppliedVersionSearch(versionSearchText.trim());
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FeatherIcon icon="search" size="17" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  setVersionPage(0);
                  setAppliedVersionSearch(versionSearchText.trim());
                }}
              >
                Search
              </Button>
            </Stack>
          </Stack>
        </Box>

        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          {isLoadingVersions && <CircularProgress size={22} sx={{ mb: 2 }} />}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Application</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Release Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Build</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Environment</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Deployment Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Team Lead</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!isLoadingVersions && versionRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                      <Box color="text.secondary">
                        <FeatherIcon icon="package" size="28" />
                        <Typography mt={1}>No version records found.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  versionRows.map((version, index) => (
                    <TableRow key={version.id ?? index} hover>
                      <TableCell>{version.applicationName}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={version.versionNumber}
                          sx={{
                            fontWeight: 700,
                            bgcolor: "primary.light",
                            color: "primary.main",
                          }}
                        />
                      </TableCell>
                      <TableCell>{version.releaseName}</TableCell>
                      <TableCell>{version.releaseType}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={version.status}
                          sx={{
                            ...getStatusChipSx(version.status),
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>{version.buildNumber}</TableCell>
                      <TableCell>{version.environment}</TableCell>
                      <TableCell>
                        {formatDateTime(version.deploymentDate)}
                      </TableCell>
                      <TableCell>{version.teamLeadName}</TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="View complete version">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewVersion(version)}
                                disabled={isLoadingVersionDetails}
                              >
                                <FeatherIcon icon="eye" size="17" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit version">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditVersion(version)}
                                disabled={isLoadingVersionDetails}
                              >
                                <FeatherIcon icon="edit-2" size="17" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete version">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(version)}
                            >
                              <FeatherIcon icon="trash-2" size="17" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={versionCount}
            page={versionPage}
            onPageChange={(event, newPage) => setVersionPage(newPage)}
            rowsPerPage={versionRowsPerPage}
            onRowsPerPageChange={(event) => {
              setVersionRowsPerPage(Number(event.target.value));
              setVersionPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardContent>
      </Card>

      <Dialog
        open={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "94vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.25,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "primary.light",
                  color: "primary.main",
                }}
              >
                <FeatherIcon
                  icon="package"
                  size="19"
                />
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                >
                  Application Version Details
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Complete release, people, features, database,
                  migration, checklist, and audit information
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() => setIsViewOpen(false)}
              aria-label="Close version details"
            >
              <FeatherIcon icon="x" size="20" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: { xs: 1.5, md: 2.5 },
            bgcolor: "action.hover",
          }}
        >
          {viewDetails && (() => {
            const version =
              getValue(
                viewDetails,
                "version",
                "Version",
              ) || {};

            const participants =
              getCollection(
                viewDetails,
                "participants",
                "Participants",
              );

            const features =
              getCollection(
                viewDetails,
                "features",
                "Features",
              );

            const databaseObjects =
              getCollection(
                viewDetails,
                "databaseObjects",
                "DatabaseObjects",
              );

            const databaseObjectDetails =
              getCollection(
                viewDetails,
                "databaseObjectDetails",
                "DatabaseObjectDetails",
              );

            const migrationObjects =
              getCollection(
                viewDetails,
                "migrationObjects",
                "MigrationObjects",
              );

            const migrationObjectDetails =
              getCollection(
                viewDetails,
                "migrationObjectDetails",
                "MigrationObjectDetails",
              );

            const checklistItems =
              getCollection(
                viewDetails,
                "checklistItems",
                "ChecklistItems",
              );

            const developers = participants.filter(
              (item) =>
                String(
                  getValue(
                    item,
                    "participantType",
                    "ParticipantType",
                  ) || "",
                ).toUpperCase() === "DEVELOPER",
            );

            const testers = participants.filter(
              (item) =>
                String(
                  getValue(
                    item,
                    "participantType",
                    "ParticipantType",
                  ) || "",
                ).toUpperCase() === "TESTER",
            );

            const approvers = participants.filter(
              (item) =>
                String(
                  getValue(
                    item,
                    "participantType",
                    "ParticipantType",
                  ) || "",
                ).toUpperCase() === "APPROVER",
            );

            const getDatabaseDetails = (parentId) =>
              databaseObjectDetails
                .filter(
                  (detail) =>
                    String(
                      getValue(
                        detail,
                        "applicationVersionDatabaseObjectId",
                        "ApplicationVersionDatabaseObjectId",
                      ),
                    ) === String(parentId),
                )
                .sort(
                  (first, second) =>
                    Number(
                      getValue(
                        first,
                        "sortOrder",
                        "SortOrder",
                      ) || 0,
                    ) -
                    Number(
                      getValue(
                        second,
                        "sortOrder",
                        "SortOrder",
                      ) || 0,
                    ),
                );

            const getMigrationDetails = (parentId) =>
              migrationObjectDetails
                .filter(
                  (detail) =>
                    String(
                      getValue(
                        detail,
                        "applicationVersionDataMigrationObjectId",
                        "ApplicationVersionDataMigrationObjectId",
                      ),
                    ) === String(parentId),
                )
                .sort(
                  (first, second) =>
                    Number(
                      getValue(
                        first,
                        "sortOrder",
                        "SortOrder",
                      ) || 0,
                    ) -
                    Number(
                      getValue(
                        second,
                        "sortOrder",
                        "SortOrder",
                      ) || 0,
                    ),
                );

            const databaseGroups = [
              {
                title: "Tables Added",
                icon: "plus-square",
                objectType: "TABLE",
                changeType: "ADDED",
              },
              {
                title: "Tables Modified",
                icon: "edit-3",
                objectType: "TABLE",
                changeType: "MODIFIED",
              },
              {
                title: "Stored Procedures Added",
                icon: "plus-circle",
                objectType: "STORED_PROCEDURE",
                changeType: "ADDED",
              },
              {
                title: "Stored Procedures Modified",
                icon: "edit",
                objectType: "STORED_PROCEDURE",
                changeType: "MODIFIED",
              },
            ].map((group) => ({
              ...group,
              rows: databaseObjects.filter(
                (item) =>
                  String(
                    getValue(
                      item,
                      "objectType",
                      "ObjectType",
                    ) || "",
                  ).toUpperCase() ===
                  group.objectType &&
                  String(
                    getValue(
                      item,
                      "changeType",
                      "ChangeType",
                    ) || "",
                  ).toUpperCase() ===
                  group.changeType,
              ),
            }));

            const migrationGroups = Array.from(
              migrationObjects.reduce(
                (map, item) => {
                  const objectType = String(
                    getValue(
                      item,
                      "objectType",
                      "ObjectType",
                    ) || "",
                  ).toUpperCase();

                  const groupId = String(
                    getValue(
                      item,
                      "mappingGroupId",
                      "MappingGroupId",
                    ) || "",
                  );

                  const key =
                    `${objectType}-${groupId}`;

                  if (!map.has(key)) {
                    map.set(key, {
                      key,
                      objectType,
                      mappingNumber: Number(
                        getValue(
                          item,
                          "mappingNumber",
                          "MappingNumber",
                        ) || 0,
                      ),
                      sourceDatabaseName:
                        getValue(
                          item,
                          "sourceDatabaseName",
                          "SourceDatabaseName",
                        ) || "-",
                      targetDatabaseName:
                        getValue(
                          item,
                          "targetDatabaseName",
                          "TargetDatabaseName",
                        ) || "-",
                      migrationDescription:
                        getValue(
                          item,
                          "migrationDescription",
                          "MigrationDescription",
                        ) || "",
                      sourceObjects: [],
                      targetObjects: [],
                    });
                  }

                  const group = map.get(key);

                  if (
                    !group.migrationDescription
                  ) {
                    group.migrationDescription =
                      getValue(
                        item,
                        "migrationDescription",
                        "MigrationDescription",
                      ) || "";
                  }

                  const side = String(
                    getValue(
                      item,
                      "migrationSide",
                      "MigrationSide",
                    ) || "",
                  ).toUpperCase();

                  if (side === "SOURCE") {
                    group.sourceObjects.push(item);
                  }

                  if (side === "TARGET") {
                    group.targetObjects.push(item);
                  }

                  return map;
                },
                new Map(),
              ).values(),
            ).sort(
              (first, second) =>
                first.mappingNumber -
                second.mappingNumber,
            );

            const renderPeople = (
              title,
              icon,
              people,
            ) => (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  height: "100%",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.25 }}
                >
                  <Box
                    sx={{
                      color: "primary.main",
                      display: "inline-flex",
                    }}
                  >
                    <FeatherIcon
                      icon={icon}
                      size="17"
                    />
                  </Box>

                  <Typography fontWeight={800}>
                    {title}
                  </Typography>

                  <Chip
                    size="small"
                    label={people.length}
                  />
                </Stack>

                {people.length ? (
                  <Stack
                    direction="row"
                    useFlexGap
                    flexWrap="wrap"
                    gap={0.75}
                  >
                    {people.map(
                      (item, index) => (
                        <Chip
                          key={`${getValue(
                            item,
                            "personId",
                            "PersonId",
                          )}-${index}`}
                          variant="outlined"
                          avatar={
                            <Box
                              component="span"
                              sx={{
                                width: 22,
                                height: 22,
                                display: "grid",
                                placeItems: "center",
                                borderRadius: "50%",
                                bgcolor: "primary.light",
                                fontSize: 11,
                                fontWeight: 800,
                              }}
                            >
                              {String(
                                getValue(
                                  item,
                                  "personName",
                                  "PersonName",
                                ) ||
                                getValue(
                                  item,
                                  "personId",
                                  "PersonId",
                                ) ||
                                "?",
                              )
                                .trim()
                                .charAt(0)
                                .toUpperCase()}
                            </Box>
                          }
                          label={
                            getValue(
                              item,
                              "personName",
                              "PersonName",
                            ) ||
                            `Person ID ${getValue(
                              item,
                              "personId",
                              "PersonId",
                            )}`
                          }
                        />
                      ),
                    )}
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    No {title.toLowerCase()} selected.
                  </Typography>
                )}
              </Paper>
            );

            return (
              <Stack spacing={2}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.75, md: 2.25 },
                    borderRadius: 1.5,
                    background:
                      "linear-gradient(135deg, rgba(227,247,250,0.95), rgba(255,255,255,1))",
                  }}
                >
                  <Stack
                    direction={{
                      xs: "column",
                      md: "row",
                    }}
                    justifyContent="space-between"
                    alignItems={{
                      xs: "flex-start",
                      md: "center",
                    }}
                    spacing={2}
                  >
                    <Box>
                      <Typography
                        variant="overline"
                        color="primary.main"
                        fontWeight={800}
                      >
                        {getValue(
                          version,
                          "applicationName",
                          "ApplicationName",
                        ) || "Application"}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        useFlexGap
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Typography
                          variant="h5"
                          fontWeight={900}
                        >
                          {getValue(
                            version,
                            "releaseName",
                            "ReleaseName",
                          ) || "Version release"}
                        </Typography>

                        <Chip
                          size="small"
                          label={`v${getValue(
                            version,
                            "versionNumber",
                            "VersionNumber",
                          ) || "-"}`}
                          color="primary"
                        />

                        <Chip
                          size="small"
                          label={
                            getValue(
                              version,
                              "status",
                              "Status",
                            ) || "-"
                          }
                          sx={getStatusChipSx(
                            getValue(
                              version,
                              "status",
                              "Status",
                            ),
                          )}
                        />
                      </Stack>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.65 }}
                      >
                        {getValue(
                          version,
                          "versionDescription",
                          "VersionDescription",
                        ) || "No version description provided."}
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      useFlexGap
                      flexWrap="wrap"
                      gap={0.75}
                    >
                      <Chip
                        icon={
                          <FeatherIcon
                            icon="server"
                            size="14"
                          />
                        }
                        label={
                          getValue(
                            version,
                            "environment",
                            "Environment",
                          ) || "-"
                        }
                        variant="outlined"
                      />

                      <Chip
                        icon={
                          <FeatherIcon
                            icon="calendar"
                            size="14"
                          />
                        }
                        label={formatDateTime(
                          getValue(
                            version,
                            "deploymentDate",
                            "DeploymentDate",
                            "deploymentDateTime",
                            "DeploymentDateTime",
                          ),
                        )}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                </Paper>

                <VersionViewSection
                  icon="info"
                  title="Release Information"
                  subtitle="Complete version and deployment metadata"
                >
                  <Grid container spacing={1.25}>
                    <VersionViewField
                      label="Application Version ID"
                      value={getValue(
                        version,
                        "applicationVersionId",
                        "ApplicationVersionId",
                      )}
                      icon="hash"
                    />

                    <VersionViewField
                      label="Application ID"
                      value={getValue(
                        version,
                        "applicationId",
                        "ApplicationId",
                      )}
                      icon="grid"
                    />

                    <VersionViewField
                      label="Application"
                      value={getValue(
                        version,
                        "applicationName",
                        "ApplicationName",
                      )}
                      icon="monitor"
                    />

                    <VersionViewField
                      label="Version Number"
                      value={getValue(
                        version,
                        "versionNumber",
                        "VersionNumber",
                      )}
                      icon="tag"
                    />

                    <VersionViewField
                      label="Release Name"
                      value={getValue(
                        version,
                        "releaseName",
                        "ReleaseName",
                      )}
                      icon="package"
                    />

                    <VersionViewField
                      label="Release Type"
                      value={getValue(
                        version,
                        "releaseType",
                        "ReleaseType",
                      )}
                      icon="layers"
                    />

                    <VersionViewField
                      label="Status"
                      value={getValue(
                        version,
                        "status",
                        "Status",
                      )}
                      icon="activity"
                    />

                    <VersionViewField
                      label="Build Number"
                      value={getValue(
                        version,
                        "buildNumber",
                        "BuildNumber",
                      )}
                      icon="box"
                      mono
                    />

                    <VersionViewField
                      label="Environment"
                      value={getValue(
                        version,
                        "environment",
                        "Environment",
                      )}
                      icon="server"
                    />

                    <VersionViewField
                      label="Deployment Date & Time"
                      value={formatDateTime(
                        getValue(
                          version,
                          "deploymentDate",
                          "DeploymentDate",
                          "deploymentDateTime",
                          "DeploymentDateTime",
                        ),
                      )}
                      icon="calendar"
                    />

                    <VersionViewField
                      label="Git Branch"
                      value={getValue(
                        version,
                        "gitBranch",
                        "GitBranch",
                      )}
                      icon="git-branch"
                      mono
                    />

                    <VersionViewField
                      label="Has Data Migration"
                      value={
                        getValue(
                          version,
                          "hasDataMigration",
                          "HasDataMigration",
                        )
                          ? "Yes"
                          : "No"
                      }
                      icon="repeat"
                    />

                    <VersionViewField
                      label="Active"
                      value={
                        getValue(
                          version,
                          "isActive",
                          "IsActive",
                        )
                          ? "Yes"
                          : "No"
                      }
                      icon="check-circle"
                    />

                    <VersionViewField
                      label="Team Lead"
                      value={`${getValue(
                        version,
                        "teamLeadName",
                        "TeamLeadName",
                      ) || "-"} (ID: ${getValue(
                        version,
                        "teamLeadId",
                        "TeamLeadId",
                        "teamLeadPersonId",
                        "TeamLeadPersonId",
                      ) || "-"
                        })`}
                      icon="users"
                    />
                  </Grid>
                </VersionViewSection>

                <VersionViewSection
                  icon="file-text"
                  title="Description & Remarks"
                  subtitle="Release purpose and additional notes"
                >
                  <Grid container spacing={1.25}>
                    <VersionViewField
                      label="Version Description"
                      value={getValue(
                        version,
                        "versionDescription",
                        "VersionDescription",
                      )}
                      icon="align-left"
                      fullWidth
                      linkify
                    />

                    <VersionViewField
                      label="Remarks"
                      value={getValue(
                        version,
                        "remarks",
                        "Remarks",
                      )}
                      icon="message-square"
                      fullWidth
                    />
                  </Grid>
                </VersionViewSection>

                <VersionViewSection
                  icon="users"
                  title="Version Participants"
                  subtitle="People responsible for development, testing, and approval"
                  count={participants.length}
                >
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} md={4}>
                      {renderPeople(
                        "Developers",
                        "code",
                        developers,
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      {renderPeople(
                        "Testers",
                        "check-square",
                        testers,
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      {renderPeople(
                        "Approvers",
                        "user-check",
                        approvers,
                      )}
                    </Grid>
                  </Grid>
                </VersionViewSection>

                <VersionViewSection
                  icon="list"
                  title="Features"
                  subtitle="Modules, submodules, and delivered functionality"
                  count={features.length}
                >
                  {features.length ? (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width={60}>
                              #
                            </TableCell>
                            <TableCell>
                              Module
                            </TableCell>
                            <TableCell>
                              Submodule
                            </TableCell>
                            <TableCell>
                              Feature Description
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {features.map(
                            (item, index) => (
                              <TableRow
                                key={
                                  getValue(
                                    item,
                                    "applicationVersionFeatureId",
                                    "ApplicationVersionFeatureId",
                                  ) || index
                                }
                              >
                                <TableCell>
                                  {index + 1}
                                </TableCell>

                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                  >
                                    {getValue(
                                      item,
                                      "moduleName",
                                      "ModuleName",
                                    ) || "-"}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ID:{" "}
                                    {getValue(
                                      item,
                                      "moduleId",
                                      "ModuleId",
                                    ) || "-"}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Typography variant="body2">
                                    {getValue(
                                      item,
                                      "subModuleName",
                                      "SubModuleName",
                                    ) || "Not selected"}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ID:{" "}
                                    {getValue(
                                      item,
                                      "subModuleId",
                                      "SubModuleId",
                                    ) || "-"}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  {getValue(
                                    item,
                                    "feature",
                                    "Feature",
                                    "featureDescription",
                                    "FeatureDescription",
                                  ) || "-"}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <VersionViewEmpty message="No features were added to this version." />
                  )}
                </VersionViewSection>

                <VersionViewSection
                  icon="database"
                  title="Database Changes"
                  subtitle="Added and modified tables and stored procedures"
                  count={databaseObjects.length}
                >
                  {databaseObjects.length ? (
                    <Stack spacing={1.5}>
                      {databaseGroups
                        .filter(
                          (group) =>
                            group.rows.length > 0,
                        )
                        .map((group) => (
                          <Paper
                            key={`${group.objectType}-${group.changeType}`}
                            variant="outlined"
                            sx={{
                              overflow: "hidden",
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{
                                px: 1.5,
                                py: 1,
                                bgcolor: "action.hover",
                                borderBottom:
                                  "1px solid",
                                borderColor:
                                  "divider",
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <FeatherIcon
                                  icon={group.icon}
                                  size="16"
                                />

                                <Typography
                                  fontWeight={800}
                                >
                                  {group.title}
                                </Typography>
                              </Stack>

                              <Chip
                                size="small"
                                label={
                                  group.rows.length
                                }
                              />
                            </Stack>

                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>
                                      Database Object
                                    </TableCell>
                                    <TableCell>
                                      Description
                                    </TableCell>
                                    <TableCell>
                                      Related Details
                                    </TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {group.rows.map(
                                    (item, index) => {
                                      const objectId =
                                        getValue(
                                          item,
                                          "applicationVersionDatabaseObjectId",
                                          "ApplicationVersionDatabaseObjectId",
                                        );

                                      const databaseName =
                                        getValue(
                                          item,
                                          "databaseName",
                                          "DatabaseName",
                                        );

                                      const schemaName =
                                        getValue(
                                          item,
                                          "schemaName",
                                          "SchemaName",
                                        ) || "dbo";

                                      const objectName =
                                        getValue(
                                          item,
                                          "objectName",
                                          "ObjectName",
                                        ) || "-";

                                      const qualifiedName =
                                        databaseName
                                          ? `[${databaseName}].[${schemaName}].[${objectName}]`
                                          : `[${schemaName}].[${objectName}]`;

                                      return (
                                        <TableRow
                                          key={
                                            objectId ||
                                            index
                                          }
                                        >
                                          <TableCell>
                                            <Typography
                                              variant="body2"
                                              fontWeight={700}
                                              sx={{
                                                fontFamily:
                                                  "Consolas, Monaco, monospace",
                                              }}
                                            >
                                              {
                                                qualifiedName
                                              }
                                            </Typography>

                                            <Stack
                                              direction="row"
                                              spacing={0.5}
                                              sx={{
                                                mt: 0.5,
                                              }}
                                            >
                                              <Chip
                                                size="small"
                                                label={
                                                  getValue(
                                                    item,
                                                    "objectType",
                                                    "ObjectType",
                                                  ) || "-"
                                                }
                                                variant="outlined"
                                              />

                                              <Chip
                                                size="small"
                                                label={
                                                  getValue(
                                                    item,
                                                    "changeType",
                                                    "ChangeType",
                                                  ) || "-"
                                                }
                                                variant="outlined"
                                              />
                                            </Stack>
                                          </TableCell>

                                          <TableCell>
                                            {getValue(
                                              item,
                                              "description",
                                              "Description",
                                            ) || "-"}
                                          </TableCell>

                                          <TableCell>
                                            {renderVersionDetailChips(
                                              getDatabaseDetails(
                                                objectId,
                                              ),
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    },
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        ))}
                    </Stack>
                  ) : (
                    <VersionViewEmpty message="No database changes were recorded." />
                  )}
                </VersionViewSection>

                <VersionViewSection
                  icon="repeat"
                  title="Data Migration"
                  subtitle="Source-to-target table and stored procedure mappings"
                  count={migrationGroups.length}
                >
                  {migrationGroups.length ? (
                    <Stack spacing={1.5}>
                      {migrationGroups.map(
                        (group) => (
                          <Paper
                            key={group.key}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction={{
                                xs: "column",
                                md: "row",
                              }}
                              alignItems={{
                                xs: "flex-start",
                                md: "center",
                              }}
                              justifyContent="space-between"
                              spacing={1}
                              sx={{ mb: 1.5 }}
                            >
                              <Box>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  useFlexGap
                                  flexWrap="wrap"
                                  gap={0.75}
                                >
                                  <Typography
                                    fontWeight={900}
                                  >
                                    Mapping #
                                    {
                                      group.mappingNumber
                                    }
                                  </Typography>

                                  <Chip
                                    size="small"
                                    label={
                                      group.objectType ===
                                        "TABLE"
                                        ? "Table"
                                        : "Stored Procedure"
                                    }
                                    color="primary"
                                    variant="outlined"
                                  />

                                  <Chip
                                    size="small"
                                    label={getMigrationRelationLabel(
                                      group
                                        .sourceObjects
                                        .length,
                                      group
                                        .targetObjects
                                        .length,
                                    )}
                                    variant="outlined"
                                  />
                                </Stack>

                                {group.migrationDescription && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {
                                      group.migrationDescription
                                    }
                                  </Typography>
                                )}
                              </Box>

                              <Stack
                                direction="row"
                                alignItems="center"
                                useFlexGap
                                flexWrap="wrap"
                                gap={0.75}
                              >
                                <Chip
                                  icon={
                                    <FeatherIcon
                                      icon="database"
                                      size="13"
                                    />
                                  }
                                  label={
                                    group.sourceDatabaseName
                                  }
                                  variant="outlined"
                                />

                                <FeatherIcon
                                  icon="arrow-right"
                                  size="17"
                                />

                                <Chip
                                  icon={
                                    <FeatherIcon
                                      icon="database"
                                      size="13"
                                    />
                                  }
                                  label={
                                    group.targetDatabaseName
                                  }
                                  variant="outlined"
                                />
                              </Stack>
                            </Stack>

                            <Grid
                              container
                              spacing={1.25}
                            >
                              {[
                                {
                                  title:
                                    "Old / Source Objects",
                                  databaseName:
                                    group.sourceDatabaseName,
                                  rows:
                                    group.sourceObjects,
                                },
                                {
                                  title:
                                    "New / Target Objects",
                                  databaseName:
                                    group.targetDatabaseName,
                                  rows:
                                    group.targetObjects,
                                },
                              ].map((side) => (
                                <Grid
                                  item
                                  xs={12}
                                  lg={6}
                                  key={side.title}
                                >
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 1.25,
                                      height: "100%",
                                      bgcolor:
                                        "action.hover",
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={800}
                                      sx={{ mb: 1 }}
                                    >
                                      {side.title}
                                    </Typography>

                                    <Stack spacing={1}>
                                      {side.rows.map(
                                        (
                                          item,
                                          index,
                                        ) => {
                                          const objectId =
                                            getValue(
                                              item,
                                              "applicationVersionDataMigrationObjectId",
                                              "ApplicationVersionDataMigrationObjectId",
                                            );

                                          const schemaName =
                                            getValue(
                                              item,
                                              "schemaName",
                                              "SchemaName",
                                            ) || "dbo";

                                          const objectName =
                                            getValue(
                                              item,
                                              "objectName",
                                              "ObjectName",
                                            ) || "-";

                                          return (
                                            <Paper
                                              key={
                                                objectId ||
                                                index
                                              }
                                              variant="outlined"
                                              sx={{
                                                p: 1.25,
                                                bgcolor:
                                                  "background.paper",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                fontWeight={800}
                                                sx={{
                                                  fontFamily:
                                                    "Consolas, Monaco, monospace",
                                                }}
                                              >
                                                {`[${side.databaseName}].[${schemaName}].[${objectName}]`}
                                              </Typography>

                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                  mt: 0.5,
                                                  mb: 0.85,
                                                }}
                                              >
                                                {getValue(
                                                  item,
                                                  "description",
                                                  "Description",
                                                ) || "-"}
                                              </Typography>

                                              {renderVersionDetailChips(
                                                getMigrationDetails(
                                                  objectId,
                                                ),
                                              )}
                                            </Paper>
                                          );
                                        },
                                      )}
                                    </Stack>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                        ),
                      )}
                    </Stack>
                  ) : (
                    <VersionViewEmpty message="No data migration mappings were recorded." />
                  )}
                </VersionViewSection>

                <VersionViewSection
                  icon="check-circle"
                  title="Deployment Checklist"
                  subtitle="Completed release-readiness checks"
                  count={checklistItems.length}
                >
                  {checklistItems.length ? (
                    <Grid container spacing={1.25}>
                      {checklistItems.map(
                        (item, index) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            lg={4}
                            key={`${getValue(
                              item,
                              "checklistKey",
                              "ChecklistKey",
                            )}-${index}`}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.35,
                                height: "100%",
                                borderColor:
                                  "success.light",
                                bgcolor:
                                  "success.light",
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                              >
                                <Box
                                  sx={{
                                    color:
                                      "success.dark",
                                    mt: 0.15,
                                  }}
                                >
                                  <FeatherIcon
                                    icon="check-circle"
                                    size="18"
                                  />
                                </Box>

                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight={800}
                                  >
                                    {getValue(
                                      item,
                                      "checklistLabelSnapshot",
                                      "ChecklistLabelSnapshot",
                                    ) ||
                                      getValue(
                                        item,
                                        "checklistKey",
                                        "ChecklistKey",
                                      )}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{
                                      mt: 0.35,
                                      wordBreak:
                                        "break-word",
                                    }}
                                  >
                                    Key:{" "}
                                    {getValue(
                                      item,
                                      "checklistKey",
                                      "ChecklistKey",
                                    ) || "-"}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Selected by ID:{" "}
                                    {getValue(
                                      item,
                                      "selectedByPersonId",
                                      "SelectedByPersonId",
                                    ) || "-"}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Selected at:{" "}
                                    {formatDateTime(
                                      getValue(
                                        item,
                                        "selectedAt",
                                        "SelectedAt",
                                      ),
                                    )}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          </Grid>
                        ),
                      )}
                    </Grid>
                  ) : (
                    <VersionViewEmpty message="No deployment checklist items were completed." />
                  )}
                </VersionViewSection>

                <VersionViewSection
                  icon="clock"
                  title="Audit & Concurrency"
                  subtitle="Creation, update, and row-version information"
                >
                  <Grid container spacing={1.25}>
                    <VersionViewField
                      label="Created By Person ID"
                      value={getValue(
                        version,
                        "createdBy",
                        "CreatedBy",
                      )}
                      icon="user-plus"
                    />

                    <VersionViewField
                      label="Created At"
                      value={formatDateTime(
                        getValue(
                          version,
                          "createdAt",
                          "CreatedAt",
                        ),
                      )}
                      icon="calendar"
                    />

                    <VersionViewField
                      label="Updated By Person ID"
                      value={getValue(
                        version,
                        "updatedBy",
                        "UpdatedBy",
                      )}
                      icon="user"
                    />

                    <VersionViewField
                      label="Updated At"
                      value={formatDateTime(
                        getValue(
                          version,
                          "updatedAt",
                          "UpdatedAt",
                        ),
                      )}
                      icon="clock"
                    />

                    <VersionViewField
                      label="RowVersion"
                      value={getValue(
                        version,
                        "rowVersion",
                        "RowVersion",
                      )}
                      icon="shield"
                      mono
                    />
                  </Grid>
                </VersionViewSection>
              </Stack>
            );
          })()}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={() => setIsViewOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !isDeletingVersion && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Application Version</DialogTitle>
        <DialogContent>
          <Typography>
            Delete version <strong>{deleteTarget?.versionNumber}</strong> for
            {" "}<strong>{deleteTarget?.applicationName}</strong>? This performs a soft delete.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={isDeletingVersion}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteVersion}
            disabled={isDeletingVersion}
            startIcon={
              isDeletingVersion ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FeatherIcon icon="trash-2" size="16" />
              )
            }
          >
            {isDeletingVersion ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VersionForm;
